import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toggleActiveTab } from '../../store/statsSlice.js';
import { setEditedEvent } from '../../store/eventsSlice';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import CustomDropdown from '../../components/components/custom_list/customDropdown';
import ApiService from '../../components/api_services/apiService';
import { mapApiUtcToPolishTime } from '../../utils/dateTime';
import {
    DateRangePicker,
    formatStatNumber,
} from './stats.ui';
import EventDetailsStatsSection from './stats.event-details.section';
import EventsStatsSection from './stats.events.section';
import OrganizersStatsSection from './stats.organizers.section';
import RankingsStatsSection from './stats.rankings.section';
import UsersStatsSection from './stats.users.section';
import {
    EVENT_DETAIL_TAB,
    EVENT_GENDER_CARD_COLORS,
    EVENT_INFO_CONTENT,
    GENDER_COLORS,
    GENDER_LABELS,
    LABELS,
    MONTH_OPTIONS,
    ORGANIZER_NAME_FALLBACK,
    RANGE_OPTIONS,
    RANKING_LIST_LIMIT,
    YEAR_OPTIONS,
} from './stats.constants';
import './Stats.css';
const isCalendarRange = (range) => range === 'RANGE' || range === 'WEEK';
const clampValue = (value, min = 0) => Math.max(min, Math.round(value));
const isObjectLike = (value) => value !== null && typeof value === 'object';

const areValuesEqual = (left, right) => {
    if (Object.is(left, right)) return true;
    if (!isObjectLike(left) || !isObjectLike(right)) return false;
    if (Array.isArray(left) !== Array.isArray(right)) return false;

    if (Array.isArray(left)) {
        if (left.length !== right.length) return false;
        for (let index = 0; index < left.length; index += 1) {
            if (!areValuesEqual(left[index], right[index])) return false;
        }
        return true;
    }

    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;

    for (const key of leftKeys) {
        if (!Object.prototype.hasOwnProperty.call(right, key)) return false;
        if (!areValuesEqual(left[key], right[key])) return false;
    }

    return true;
};

const updateStateIfChanged = (setter, nextValue) => {
    setter((previous) => (areValuesEqual(previous, nextValue) ? previous : nextValue));
};
const normalizeChartLabel = (value) => {
    const raw = String(value ?? '').trim();
    const upper = raw.toUpperCase();
    const invalidMarkers = ['UNKNOWN', 'UNKOWN', 'UNKONWN'];
    if (invalidMarkers.some((marker) => upper.includes(marker))) return '';
    return raw;
};

const padNumber = (value) => String(value).padStart(2, '0');
const toDateInputValue = (date) => (
    `${date.getUTCFullYear()}-${padNumber(date.getUTCMonth() + 1)}-${padNumber(date.getUTCDate())}`
);
const parseDateValue = (value) => {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const toUtcDayStamp = (date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
const isSameUtcDate = (left, right) => (
    Boolean(left && right) && toUtcDayStamp(left) === toUtcDayStamp(right)
);

const getCurrentWeekRangeValues = () => {
    const today = new Date();
    const weekDayIndex = (today.getUTCDay() + 6) % 7;
    const start = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - weekDayIndex, 0, 0, 0)
    );
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + 6, 0, 0, 0));

    return {
        start: toDateInputValue(start),
        end: toDateInputValue(end),
    };
};
const DEFAULT_WEEK_RANGE = getCurrentWeekRangeValues();

const formatDatePl = (date) => (
    `${padNumber(date.getUTCDate())}.${padNumber(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`
);
const roundToOneDecimal = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.round(parsed * 10) / 10;
};

const normalizeDateRangeValues = (startValue, endValue) => {
    const parsedStart = parseDateValue(startValue);
    const parsedEnd = parseDateValue(endValue);
    const start = parsedStart || parsedEnd;
    const end = parsedEnd || parsedStart;

    if (!start || !end) {
        return {
            startDate: null,
            endDate: null,
            startValue: '',
            endValue: '',
        };
    }

    const startStamp = toUtcDayStamp(start);
    const endStamp = toUtcDayStamp(end);
    const normalizedStart = startStamp <= endStamp ? start : end;
    const normalizedEnd = startStamp <= endStamp ? end : start;

    return {
        startDate: normalizedStart,
        endDate: normalizedEnd,
        startValue: toDateInputValue(normalizedStart),
        endValue: toDateInputValue(normalizedEnd),
    };
};

const normalizeGenderValue = (value) => (
    typeof value === 'number' ? { percent: value, count: null } : (value || { percent: 0, count: 0 })
);

const normalizeGenderBreakdown = (gender) => ({
    K: normalizeGenderValue(gender?.K),
    M: normalizeGenderValue(gender?.M),
    I: normalizeGenderValue(gender?.I),
});

const scaleGenderBreakdownCounts = (gender, factor) => (
    Object.fromEntries(
        Object.entries(gender || {}).map(([key, value]) => {
            const normalized = normalizeGenderValue(value);
            return [
                key,
                {
                    ...normalized,
                    count: normalized.count != null ? clampValue(normalized.count * factor) : normalized.count,
                },
            ];
        })
    )
);

const getGenderRatioForFilter = (genderData, selectedGender) => {
    if (selectedGender === 'ALL') return 1;
    const selectedEntry = (genderData || []).find((item) => item.key === selectedGender);
    return selectedEntry ? (selectedEntry.percent / 100) : 0;
};

const applyGenderFilterToShare = (genderData, selectedGender) => {
    if (selectedGender === 'ALL') return genderData;

    return (genderData || []).map((item) => {
        if (item.key === selectedGender) {
            return { ...item, percent: 100 };
        }

        return { ...item, percent: 0, count: 0 };
    });
};

const applyGenderFilterToSeries = (series, ratio) => (
    (series || []).map((item) => {
        if (typeof item.total === 'number' || typeof item.confirmed === 'number') {
            const total = clampValue((item.total || 0) * ratio);
            const confirmed = clampValue((item.confirmed || 0) * ratio);
            const value = total > 0 ? clampValue((confirmed / total) * 100, 0) : 0;
            return { ...item, total, confirmed, value };
        }

        if (typeof item.value === 'number') {
            return { ...item, value: clampValue(item.value * ratio) };
        }

        return item;
    })
);

const getRangeDates = (range, year, month, weekStartDate, weekEndDate) => {
    const now = new Date();
    const selectedYear = Number(year) || now.getUTCFullYear();
    const selectedMonth = Math.max(1, Math.min(12, Number(month) || 1));

    if (range === 'YEAR') {
        const from = new Date(Date.UTC(selectedYear, 0, 1, 0, 0, 0));
        const to = new Date(Date.UTC(selectedYear, 11, 31, 23, 59, 59));
        return { from, to };
    }

    if (range === 'MONTH') {
        const from = new Date(Date.UTC(selectedYear, selectedMonth - 1, 1, 0, 0, 0));
        const to = new Date(Date.UTC(selectedYear, selectedMonth, 0, 23, 59, 59));
        return { from, to };
    }

    if (isCalendarRange(range)) {
        const normalizedWeek = normalizeDateRangeValues(weekStartDate, weekEndDate);
        const fallbackWeek = normalizeDateRangeValues(DEFAULT_WEEK_RANGE.start, DEFAULT_WEEK_RANGE.end);
        const weekStart = normalizedWeek.startDate || fallbackWeek.startDate;
        const weekEnd = normalizedWeek.endDate || fallbackWeek.endDate;
        const from = new Date(Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate(), 0, 0, 0));
        const to = new Date(Date.UTC(weekEnd.getUTCFullYear(), weekEnd.getUTCMonth(), weekEnd.getUTCDate(), 23, 59, 59));
        return { from, to };
    }

    const from = new Date(Date.UTC(now.getUTCFullYear() - 4, 0, 1, 0, 0, 0));
    const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59));
    return { from, to };
};

const toApiDateOnly = (date) => toDateInputValue(date);

const getStatisticsInterval = (range, from, to) => {
    if (range === 'YEAR') return 'MONTH';
    if (range === 'MONTH') return 'DAY';
    if (!isCalendarRange(range)) return 'YEAR';

    const daysCount = getDaysCountInclusive(from, to);
    const bucketType = getBucketTypeForDaysCount(daysCount);
    if (bucketType === 'day') return 'DAY';
    if (bucketType === 'week') return 'WEEK';
    return 'MONTH';
};

const buildStatisticsQuery = (range, year, month, weekStartDate, weekEndDate) => {
    const { from, to } = getRangeDates(range, year, month, weekStartDate, weekEndDate);

    return {
        dateFrom: toApiDateOnly(from),
        dateTo: toApiDateOnly(to),
        interval: getStatisticsInterval(range, from, to),
    };
};

const findMetricValueByKeys = (source, keys = []) => {
    for (const key of keys) {
        const parsed = toSafeMetricNumber(source?.[key]);
        if (parsed != null) return parsed;
    }

    return null;
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const toUtcStartOfDay = (date) => (
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0))
);

const addUtcDays = (date, days) => (
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days, 0, 0, 0))
);

const startOfUtcWeekMonday = (date) => {
    const startOfDay = toUtcStartOfDay(date);
    const weekDayIndex = (startOfDay.getUTCDay() + 6) % 7;
    return addUtcDays(startOfDay, -weekDayIndex);
};

const startOfUtcMonth = (date) => (
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0))
);

const endOfUtcMonth = (date) => (
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 0, 0, 0))
);

const getDaysCountInclusive = (from, to) => {
    const fromStamp = toUtcStartOfDay(from).getTime();
    const toStamp = toUtcStartOfDay(to).getTime();
    return Math.max(1, Math.floor((toStamp - fromStamp) / DAY_IN_MS) + 1);
};

const getBucketTypeForDaysCount = (daysCount) => {
    if (daysCount <= 31) return 'day';
    if (daysCount <= 90) return 'week';
    return 'month';
};

const buildRangeBuckets = (from, to) => {
    const fromDay = toUtcStartOfDay(from);
    const toDay = toUtcStartOfDay(to);
    const daysCount = getDaysCountInclusive(fromDay, toDay);
    const bucketType = getBucketTypeForDaysCount(daysCount);
    const buckets = [];

    if (bucketType === 'day') {
        for (let cursor = fromDay; cursor.getTime() <= toDay.getTime(); cursor = addUtcDays(cursor, 1)) {
            buckets.push({
                from: cursor,
                to: cursor,
                label: `${padNumber(cursor.getUTCDate())}.${padNumber(cursor.getUTCMonth() + 1)}`,
            });
        }

        return { buckets, bucketType, daysCount };
    }

    if (bucketType === 'week') {
        for (
            let cursor = startOfUtcWeekMonday(fromDay);
            cursor.getTime() <= toDay.getTime();
            cursor = addUtcDays(cursor, 7)
        ) {
            const weekEnd = addUtcDays(cursor, 6);
            buckets.push({
                from: cursor,
                to: weekEnd,
                label: `${padNumber(cursor.getUTCDate())}.${padNumber(cursor.getUTCMonth() + 1)}-${padNumber(weekEnd.getUTCDate())}.${padNumber(weekEnd.getUTCMonth() + 1)}`,
            });
        }

        return { buckets, bucketType, daysCount };
    }

    const includeYearInMonthLabel = fromDay.getUTCFullYear() !== toDay.getUTCFullYear();

    for (
        let cursor = startOfUtcMonth(fromDay);
        cursor.getTime() <= toDay.getTime();
        cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1, 0, 0, 0))
    ) {
        const monthLabel = MONTH_OPTIONS[cursor.getUTCMonth()]?.label || `${cursor.getUTCMonth() + 1}`;
        buckets.push({
            from: startOfUtcMonth(cursor),
            to: endOfUtcMonth(cursor),
            label: includeYearInMonthLabel ? `${monthLabel} ${cursor.getUTCFullYear()}` : monthLabel,
        });
    }

    return { buckets, bucketType, daysCount };
};

const toEntries = (data) => {
    if (!data) return [];

    if (Array.isArray(data)) {
        return data
            .map((item) => {
                if (!item) return null;
                if (Array.isArray(item)) {
                    return { date: item[0], value: item[1] };
                }
                if (typeof item === 'object') {
                    const date = normalizeChartLabel(
                        item.date || item.label || item.day || item.key || item.time || item.period
                    );
                    const value = item.value ?? item.count ?? item.total ?? item.amount;
                    return { date, value };
                }
                return null;
            })
            .filter(Boolean);
    }

    if (typeof data === 'object') {
        return Object.entries(data).map(([key, value]) => ({ date: normalizeChartLabel(key), value }));
    }

    return [];
};

const extractCollectionPayload = (response) => (
    Array.isArray(response)
        ? response
        : response?.items
            || response?.content
            || response?.data
            || response?.results
            || response?.values
            || []
);
const extractTimeSeriesPayload = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    return response?.timeSeries
        || response?.series
        || response?.data
        || response?.items
        || [];
};
const buildAverageParticipantsSeriesFromResponse = (response) => {
    const rows = extractTimeSeriesPayload(response);
    if (!Array.isArray(rows) || !rows.length) return [];

    return rows.map((item) => ({
        label: normalizeChartLabel(item?.period || item?.label || item?.date || ''),
        value: roundToOneDecimal(item?.value),
    }));
};
const extractDemographicBreakdownPayload = (response) => (
    response?.demographicBreakdown || response?.demography || null
);
const normalizeDemographicGender = (gender) => ({
    K: toSafeMetricNumber(gender?.K ?? gender?.female ?? gender?.women) ?? 0,
    M: toSafeMetricNumber(gender?.M ?? gender?.male ?? gender?.men) ?? 0,
    I: toSafeMetricNumber(gender?.I ?? gender?.other) ?? 0,
});
const resolveGenderDemography = (gender) => {
    const safeGender = normalizeDemographicGender(gender || {});
    const values = [safeGender.K, safeGender.M, safeGender.I].map((value) => Math.max(0, Number(value) || 0));
    const total = values.reduce((sum, value) => sum + value, 0);
    const toPercent = (value) => (total > 0 ? clampValue((value / total) * 100) : 0);
    return {
        total,
        rows: {
            K: { percent: toPercent(safeGender.K), count: clampValue(safeGender.K) },
            M: { percent: toPercent(safeGender.M), count: clampValue(safeGender.M) },
            I: { percent: toPercent(safeGender.I), count: clampValue(safeGender.I) },
        },
    };
};
const normalizeDemographicAges = (ageGroups) => {
    if (!ageGroups) return [];
    const normalizedEntries = Array.isArray(ageGroups)
        ? ageGroups.map((value) => [String(value || ''), 0])
        : Object.entries(ageGroups);

    const rows = normalizedEntries
        .map(([range, value]) => ({
            range: String(range || '').replace('–', '-'),
            count: toSafeMetricNumber(value) ?? 0,
        }))
        .filter((item) => Boolean(item.range));
    const total = rows.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    return rows.map((item) => ({
        ...item,
        percent: total > 0 ? clampValue((item.count / total) * 100) : 0,
    }));
};
const normalizeDemographicsPercentMap = (distribution) => ({
    K: toSafeMetricNumber(distribution?.K ?? distribution?.female ?? distribution?.women) ?? 0,
    M: toSafeMetricNumber(distribution?.M ?? distribution?.male ?? distribution?.men) ?? 0,
    I: toSafeMetricNumber(distribution?.I ?? distribution?.other) ?? 0,
});
const mapGenderRowsFromDemographics = (demographicsPayload, fallbackTotalCount = 0) => {
    const distribution = normalizeDemographicsPercentMap(demographicsPayload?.genderDistribution || {});
    const totals = demographicsPayload?.genderTotals || {};
    const countK = clampValue(totals?.K ?? 0);
    const countM = clampValue(totals?.M ?? 0);
    const countI = clampValue(totals?.I ?? 0);
    const fromTotals = countK + countM + countI;
    const fallbackTotal = Math.max(0, clampValue(fallbackTotalCount || 0));
    const total = fromTotals > 0 ? fromTotals : fallbackTotal;
    const toCount = (percent) => (total > 0 ? clampValue((total * percent) / 100) : 0);
    const toPercent = (count) => (total > 0 ? clampValue((count / total) * 100) : 0);

    const resolvedCountK = countK > 0 ? countK : toCount(distribution.K);
    const resolvedCountM = countM > 0 ? countM : toCount(distribution.M);
    const resolvedCountI = countI > 0 ? countI : toCount(distribution.I);

    return {
        K: { percent: distribution.K > 0 ? clampValue(distribution.K) : toPercent(resolvedCountK), count: resolvedCountK },
        M: { percent: distribution.M > 0 ? clampValue(distribution.M) : toPercent(resolvedCountM), count: resolvedCountM },
        I: { percent: distribution.I > 0 ? clampValue(distribution.I) : toPercent(resolvedCountI), count: resolvedCountI },
    };
};
const mapAgeRowsFromDemographicsApi = (demographicsPayload, selectedGender) => {
    if (!demographicsPayload) return [];

    const ageTotals = demographicsPayload?.ageTotals || {};
    const ageTotalsByGender = demographicsPayload?.ageTotalsByGender || {};
    const ageDistributionByGender = demographicsPayload?.ageDistributionByGender || {};
    const selectedDistribution = selectedGender !== 'ALL'
        ? ageDistributionByGender?.[selectedGender]
        : null;
    const baseDistribution = selectedDistribution || demographicsPayload?.ageDistribution || {};
    const selectedTotals = selectedGender !== 'ALL'
        ? (ageTotalsByGender?.[selectedGender] || {})
        : ageTotals;
    const ranges = Array.from(new Set([
        ...Object.keys(baseDistribution || {}),
        ...Object.keys(selectedTotals || {}),
    ]));

    const rows = ranges
        .map((range) => ({
            range: String(range || '').replace('–', '-'),
            percent: clampValue(baseDistribution?.[range] ?? 0),
            count: clampValue(selectedTotals?.[range] ?? 0),
        }))
        .filter((item) => Boolean(item.range));
    const totalFromCounts = rows.reduce((sum, item) => sum + (Number(item.count) || 0), 0);

    return rows.map((item) => ({
        ...item,
        percent: item.percent > 0
            ? item.percent
            : (totalFromCounts > 0 ? clampValue((item.count / totalFromCounts) * 100) : 0),
    }));
};
const extractEventImageHash = (source, includeLegacyImageField = false) => {
    if (!source) return null;

    const candidates = [
        source?.image_hash_code,
        source?.imageHashCode,
        source?.imageId,
        source?.imageUrl,
        source?.image_url,
    ];

    if (includeLegacyImageField) {
        candidates.push(source?.image);
    }

    let firstNonEmpty = null;
    for (const value of candidates) {
        if (value == null) continue;
        const asString = String(value);
        if (!asString) continue;
        if (!firstNonEmpty) {
            firstNonEmpty = asString;
        }
        if (asString.startsWith('_EVENT_')) {
            return asString;
        }
    }

    return firstNonEmpty;
};

const mapUnifiedRankingItems = (response, metricType) => {
    const metricTypeUpper = String(metricType || '').toUpperCase();
    const metricField = metricTypeUpper === 'JOINS' || metricTypeUpper === 'PACKAGES' ? 'packs' : 'likes';
    const items = extractCollectionPayload(response);

    return (Array.isArray(items) ? items : [])
        .map((item, index) => {
            const locationValue = item?.location || '';
            const [place, city] = String(locationValue).split(',').map((part) => part?.trim()).filter(Boolean);
            const metricValue = pickFirstMetricNumber(item?.metricValue, item?.value, item?.counting, item?.count, item?.total);
            const imageHashCode = extractEventImageHash(item);

            return {
                id: item?.eventId ?? item?.id ?? index + 1,
                name: item?.title ?? item?.name ?? 'Wydarzenie',
                place: place || item?.place || '',
                city: city || item?.city || '',
                startDateRaw: item?.eventDate || item?.date || item?.startDate || null,
                date: formatRankingDate(item?.eventDate || item?.date || item?.startDate),
                time: formatRankingTime(item?.eventDate || item?.date || item?.startDate),
                rank: Number(item?.rank) || index + 1,
                likes: metricField === 'likes' ? (metricValue ?? 0) : 0,
                packs: metricField === 'packs' ? (metricValue ?? 0) : 0,
                metricValue: metricValue ?? 0,
                imageHashCode,
            };
        });
};

const getLabelForDate = (date, range) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

    if (range === 'ALL') {
        return `${date.getUTCFullYear()}`;
    }

    if (range === 'YEAR') {
        return LABELS.YEAR[date.getUTCMonth()];
    }

    if (range === 'MONTH') {
        return `${date.getUTCDate()}`;
    }

    const weekDayIndex = (date.getUTCDay() + 6) % 7;
    return LABELS.WEEK[weekDayIndex];
};

const buildSeriesFromResponse = (data, range, options = {}) => {
    const entries = toEntries(data);

    if (isCalendarRange(range)) {
        const { year, month, startValue, endValue } = options;
        const { from, to } = getRangeDates(range, year, month, startValue, endValue);
        const { buckets, bucketType } = buildRangeBuckets(from, to);
        const sumsByBucket = new Map(
            buckets.map((bucket) => [toDateInputValue(bucket.from), 0])
        );

        entries.forEach(({ date, value }) => {
            const parsedDate = new Date(date);
            if (Number.isNaN(parsedDate.getTime())) return;

            const valueNumber = Number(value) || 0;
            const utcDate = toUtcStartOfDay(parsedDate);
            const bucketDate =
                bucketType === 'day'
                    ? utcDate
                    : bucketType === 'week'
                        ? startOfUtcWeekMonday(utcDate)
                        : startOfUtcMonth(utcDate);
            const bucketKey = toDateInputValue(bucketDate);

            if (!sumsByBucket.has(bucketKey)) return;
            sumsByBucket.set(bucketKey, (sumsByBucket.get(bucketKey) || 0) + valueNumber);
        });

        return buckets.map((bucket) => ({
            label: bucket.label,
            value: clampValue(sumsByBucket.get(toDateInputValue(bucket.from)) || 0),
        }));
    }

    const labels = LABELS[range];
    if (!labels) return [];

    const buckets = labels.reduce((acc, label) => {
        acc[label] = 0;
        return acc;
    }, {});

    entries.forEach(({ date, value }) => {
        const parsedDate = new Date(date);
        const label = getLabelForDate(parsedDate, range);
        if (!label || !(label in buckets)) return;
        buckets[label] += Number(value) || 0;
    });

    return labels.map((label) => ({ label, value: clampValue(buckets[label]) }));
};

const formatRankingDate = (dateString) => {
    if (!dateString) return '-';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return dateString;
    return `${padNumber(parsed.getDate())}.${padNumber(parsed.getMonth() + 1)}.${parsed.getFullYear()}`;
};
const formatRankingTime = (dateString) => {
    if (!dateString) return '-';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return '-';
    return `${padNumber(parsed.getHours())}:${padNumber(parsed.getMinutes())}`;
};

const toSafeMetricNumber = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(0, parsed);
};

const pickFirstMetricNumber = (...values) => {
    for (const value of values) {
        const parsed = toSafeMetricNumber(value);
        if (parsed != null) return parsed;
    }

    return null;
};

const normalizeTextValue = (value) => String(value ?? '').trim().toLowerCase();
const pickFirstDefined = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null) return value;
    }
    return null;
};
const normalizeOwnerIdKey = (value) => {
    const raw = String(value ?? '').trim();
    return raw || null;
};
const resolveOwnerIdFromEventLike = (eventLike = {}) => (
    normalizeOwnerIdKey(
        eventLike?.ownerId
        ?? eventLike?.owner_id
        ?? eventLike?.ownerUUID
        ?? eventLike?.owner_uuid
        ?? eventLike?.owner?.id
        ?? eventLike?.owner?.ownerId
        ?? eventLike?.owner?.owner_id
        ?? eventLike?.owner?.userId
        ?? eventLike?.owner?.user_id
        ?? eventLike?.userId
        ?? eventLike?.user_id
        ?? eventLike?.organizerId
        ?? eventLike?.organizer_id
        ?? eventLike?.organiserId
        ?? eventLike?.organiser_id
        ?? eventLike?.event?.ownerId
        ?? eventLike?.event?.owner_id
        ?? eventLike?.event?.owner?.id
    )
);
const normalizeEventIdKey = (value) => {
    const raw = String(value ?? '').trim();
    return raw || null;
};
const hasUsableEventName = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return false;
    const normalized = raw.toLowerCase();
    return normalized !== 'wydarzenie' && normalized !== 'brak danych';
};
const hasOrganizerName = (value) => {
    const normalized = normalizeTextValue(value);
    return Boolean(normalized) && normalized !== normalizeTextValue(ORGANIZER_NAME_FALLBACK);
};
const isUuidLike = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value ?? ''));
const pickRawImageId = (...values) => {
    for (const value of values) {
        if (value == null) continue;
        const asString = String(value);
        if (!asString) continue;
        return asString;
    }

    return null;
};
const hasEventImagePrefix = (value) => String(value ?? '').startsWith('_EVENT_');
const isLikelyDirectImageUrl = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return false;
    return raw.startsWith('http://')
        || raw.startsWith('https://')
        || raw.startsWith('/open/')
        || raw.startsWith('/api/')
        || raw.startsWith('blob:')
        || raw.startsWith('data:');
};
const mapRankingEventMetaFromApi = (source = {}) => ({
    name: pickFirstDefined(source?.name, source?.title, source?.eventName, source?.event_name, source?.eventTitle) || '',
    place: pickFirstDefined(source?.place, source?.placeName, source?.place_name) || '',
    city: pickFirstDefined(source?.city, source?.cityName, source?.city_name) || '',
    startDateRaw: pickFirstDefined(
        source?.startDate,
        source?.start_date,
        source?.eventDate,
        source?.event_date,
        source?.date,
        source?.meetingDate
    ) || null,
});

const resolveNumericCategoryId = (...values) => {
    for (const value of values) {
        const parsed = Number(value);
        if (Number.isInteger(parsed) && parsed >= 0) {
            return String(parsed);
        }
    }

    return null;
};

const USERS_SUMMARY = [
    { key: 'registered', label: 'Zarejestrowani', value: '0', note: 'stan na dziś' },
    { key: 'new', label: 'Nowi', value: '0', note: 'w wybranym okresie' },
    { key: 'active', label: 'Aktywni', value: '0', note: 'w wybranym okresie' },
    { key: 'inactive', label: 'Niezalogowani', value: '0', note: 'w wybranym okresie' },
    { key: 'pack', label: 'Aktywność paczek', value: '0', note: 'dołączenie lub utworzenie' },
    { key: 'confirmed', label: 'Potwierdzone wyjścia', value: '0%', note: '0 / 0 wyjść' },
];

const USERS_PAIRED_EXTRA_SUMMARY = [
    { key: 'accountDeletion', label: 'Usunięcia konta', value: '0', note: 'w wybranym okresie' },
    { key: 'reports', label: 'Zgłoszenia', value: '0', note: 'w wybranym okresie' },
];

const ORGANIZERS_SUMMARY = [
    { key: 'registered', label: 'Zarejestrowani', value: '0', note: 'stan na dziś' },
    { key: 'new', label: 'Nowi organizatorzy', value: '0', note: 'w wybranym okresie' },
];
const RETRY_COOLDOWN_MS = 60 * 1000;
const getTrendLastValue = (series) => {
    if (!Array.isArray(series) || !series.length) return 0;
    const lastPoint = series[series.length - 1];
    return clampValue(lastPoint?.value || 0);
};


const buildCountsFromRatios = (total, ratios) => {
    const safeTotal = Math.max(0, Math.round(Number(total) || 0));
    if (!safeTotal) return ratios.map(() => 0);

    const weighted = ratios.map((ratio) => safeTotal * ratio);
    const base = weighted.map((value) => Math.floor(value));
    let remainder = safeTotal - base.reduce((sum, value) => sum + value, 0);
    const order = weighted
        .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
        .sort((left, right) => right.fraction - left.fraction);

    for (let index = 0; index < remainder; index += 1) {
        const next = order[index % order.length];
        base[next.index] += 1;
    }

    return base;
};

const addPercentToLegendItems = (items) => {
    const total = items.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
    return items.map((item) => ({
        ...item,
        percent: total > 0 ? clampValue(((Number(item?.count) || 0) / total) * 100) : 0,
    }));
};

const mapStatsEventToDetailPanelEvent = (eventLike = {}) => ({
    id: pickFirstDefined(eventLike?.id, eventLike?.eventId, eventLike?.event_id, eventLike?.uuid, eventLike?.eventUuid),
    name: pickFirstDefined(eventLike?.name, eventLike?.title, eventLike?.eventName, eventLike?.event_name, eventLike?.eventTitle) || 'Wydarzenie',
    organizerName: pickFirstDefined(
        eventLike?.organizerName,
        eventLike?.organizer,
        eventLike?.companyName,
        eventLike?.company_name,
        eventLike?.organiserName,
    ) || ORGANIZER_NAME_FALLBACK,
    ownerId: resolveOwnerIdFromEventLike(eventLike) || resolveOwnerIdFromEventLike(eventLike?.event),
    city: pickFirstDefined(eventLike?.city, eventLike?.cityName, eventLike?.city_name) || '',
    place: pickFirstDefined(eventLike?.place, eventLike?.placeName, eventLike?.place_name) || '',
    adres: pickFirstDefined(eventLike?.adres, eventLike?.address) || '',
    zipCode: pickFirstDefined(eventLike?.zipCode, eventLike?.zip_code, eventLike?.postalCode, eventLike?.postal_code) || '',
    startDate: pickFirstDefined(eventLike?.startDate, eventLike?.start_date, eventLike?.eventDate, eventLike?.event_date, eventLike?.date) || null,
    endDate: pickFirstDefined(eventLike?.endDate, eventLike?.end_date) || null,
    categoryId: pickFirstDefined(eventLike?.categoryId, eventLike?.category_id, eventLike?.eventsCategoryId),
    categoryName: pickFirstDefined(eventLike?.categoryName, eventLike?.category_name, eventLike?.eventsCategoryName) || '',
    ticketInfo: pickFirstDefined(eventLike?.ticketInfo, eventLike?.ticket_info) || '',
    ticketPrice: pickFirstDefined(eventLike?.ticketPrice, eventLike?.ticket_price) || '',
    description: pickFirstDefined(eventLike?.description, eventLike?.eventDescription) || '',
    isActive: pickFirstDefined(eventLike?.isActive, eventLike?.is_active, eventLike?.active) ?? true,
    image_hash_code: pickFirstDefined(eventLike?.image_hash_code, eventLike?.imageHashCode, eventLike?.imageUrl, eventLike?.image_url) || '',
    added_on_utc: pickFirstDefined(eventLike?.added_on_utc, eventLike?.addedOnUtc, eventLike?.createdAt, eventLike?.created_at) || null,
    status: pickFirstDefined(eventLike?.status, eventLike?.eventStatus) || 'accepted',
});

const mapStatsEventToSidebarItem = (eventLike = {}, index = 0) => {
    const source = eventLike?.event || eventLike?.data || eventLike;
    const resolvedId = normalizeEventIdKey(
        pickFirstDefined(source?.id, source?.eventId, source?.event_id, source?.uuid, source?.eventUuid)
    );
    if (!resolvedId) return null;

    const rawLocation = String(pickFirstDefined(source?.location, source?.eventLocation) || '').trim();
    const [locationPlace, locationCity] = rawLocation
        ? rawLocation.split(',').map((part) => String(part || '').trim())
        : ['', ''];
    const startDate = pickFirstDefined(
        source?.startDate,
        source?.start_date,
        source?.eventDate,
        source?.event_date,
        source?.date,
        source?.meetingDate
    ) || null;
    const endDate = pickFirstDefined(source?.endDate, source?.end_date) || null;
    const city = pickFirstDefined(source?.city, source?.cityName, source?.city_name, locationCity) || '';
    const place = pickFirstDefined(source?.place, source?.placeName, source?.place_name, locationPlace) || '';
    const resolvedName = pickFirstDefined(source?.name, source?.title, source?.eventName, source?.event_name, source?.eventTitle);

    return {
        id: resolvedId,
        rank: Number(source?.rank) || index + 1,
        name: hasUsableEventName(resolvedName) ? String(resolvedName).trim() : `Wydarzenie #${resolvedId}`,
        organizerName: pickFirstDefined(
            source?.organizerName,
            source?.organizer,
            source?.companyName,
            source?.company_name,
            source?.organiserName
        ) || ORGANIZER_NAME_FALLBACK,
        ownerId: resolveOwnerIdFromEventLike(source) || resolveOwnerIdFromEventLike(eventLike),
        city,
        place,
        categoryId: pickFirstDefined(source?.categoryId, source?.category_id, source?.eventsCategoryId, source?.eventCategoryId),
        categoryName: pickFirstDefined(source?.categoryName, source?.category_name, source?.eventsCategoryName, source?.eventCategoryName) || '',
        startDate,
        endDate,
        imageUrl: pickFirstDefined(source?.imageUrl, source?.image_url, source?.imageHashCode, source?.image_hash_code, source?.image) || '',
        imageHashCode: extractEventImageHash(source, true),
        statsPayload: eventLike,
    };
};

const upsertStatsEvent = (items, nextItem) => {
    if (!nextItem?.id) return items;
    const nextId = normalizeEventIdKey(nextItem.id);
    const existingIndex = items.findIndex((item) => normalizeEventIdKey(item.id) === nextId);
    if (existingIndex === -1) {
        return [nextItem, ...items];
    }

    const updated = [...items];
    const existingItem = updated[existingIndex];
    const existingHash = pickRawImageId(existingItem?.imageHashCode);
    const nextHash = pickRawImageId(nextItem?.imageHashCode);
    const resolvedOwnerId = resolveOwnerIdFromEventLike(nextItem) || resolveOwnerIdFromEventLike(existingItem);

    const resolvePreferredName = () => {
        if (hasUsableEventName(nextItem?.name)) return String(nextItem.name).trim();
        if (hasUsableEventName(existingItem?.name)) return String(existingItem.name).trim();
        return `Wydarzenie #${nextId}`;
    };
    const resolvePreferredOrganizer = () => {
        if (hasOrganizerName(nextItem?.organizerName)) return nextItem.organizerName;
        if (hasOrganizerName(existingItem?.organizerName)) return existingItem.organizerName;
        return ORGANIZER_NAME_FALLBACK;
    };
    const resolvePreferredDate = () => {
        const nextDate = pickFirstDefined(nextItem?.startDate, nextItem?.date);
        const existingDate = pickFirstDefined(existingItem?.startDate, existingItem?.date);
        if (parseStatsEventDateValue(nextDate)) return nextDate;
        if (parseStatsEventDateValue(existingDate)) return existingDate;
        return nextDate || existingDate || null;
    };
    const resolvePreferredPlace = () => {
        const nextPlace = String(nextItem?.place || '').trim();
        const existingPlace = String(existingItem?.place || '').trim();
        return nextPlace || existingPlace || '';
    };
    const resolvePreferredCity = () => {
        const nextCity = String(nextItem?.city || '').trim();
        const existingCity = String(existingItem?.city || '').trim();
        return nextCity || existingCity || '';
    };

    let mergedHash = nextHash ?? existingHash ?? null;
    if (existingHash && nextHash) {
        const existingHasPrefix = hasEventImagePrefix(existingHash);
        const nextHasPrefix = hasEventImagePrefix(nextHash);
        if (existingHasPrefix && !nextHasPrefix) {
            mergedHash = existingHash;
        }
    }

    updated[existingIndex] = {
        ...existingItem,
        ...nextItem,
        ownerId: resolvedOwnerId,
        name: resolvePreferredName(),
        organizerName: resolvePreferredOrganizer(),
        startDate: resolvePreferredDate(),
        place: resolvePreferredPlace(),
        city: resolvePreferredCity(),
        imageHashCode: mergedHash,
    };
    return updated;
};

const parseStatsEventDateValue = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;

    const parsedNative = new Date(raw);
    if (!Number.isNaN(parsedNative.getTime())) return parsedNative;

    const polishDateMatch = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!polishDateMatch) return null;

    const [, day, month, year, hours = '0', minutes = '0', seconds = '0'] = polishDateMatch;
    const parsedPolish = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds),
    );
    return Number.isNaN(parsedPolish.getTime()) ? null : parsedPolish;
};

const formatSidebarEventDate = (eventItem) => {
    const parsed = parseStatsEventDateValue(eventItem?.startDate || eventItem?.date || '');
    if (!parsed) return '-';
    return `${padNumber(parsed.getDate())}.${padNumber(parsed.getMonth() + 1)}.${parsed.getFullYear()}`;
};

const formatSidebarEventDateLabel = (eventItem) => {
    const parsed = parseStatsEventDateValue(eventItem?.startDate || eventItem?.date || '');
    if (!parsed) return '-';

    const raw = new Intl.DateTimeFormat('pl-PL', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(parsed);

    const withoutWeekdayDot = raw.replace(/\./g, '');
    return withoutWeekdayDot.charAt(0).toUpperCase() + withoutWeekdayDot.slice(1);
};

const formatSidebarEventTimeLabel = (eventItem) => {
    const parsed = parseStatsEventDateValue(eventItem?.startDate || eventItem?.date || '');
    if (!parsed) return '-';

    return `${padNumber(parsed.getHours())}:${padNumber(parsed.getMinutes())}`;
};

function Stats() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const activeTab = useSelector((state) => state.stats.activeTab);
    const accessToken = useSelector((state) => state.tokens.accessToken);

    const [usersRange, setUsersRange] = useState('ALL');
    const [usersYear, setUsersYear] = useState('2026');
    const [usersMonth, setUsersMonth] = useState('01');
    const [usersWeekStartDate, setUsersWeekStartDate] = useState(DEFAULT_WEEK_RANGE.start);
    const [usersWeekEndDate, setUsersWeekEndDate] = useState(DEFAULT_WEEK_RANGE.end);
    const [usersGender, setUsersGender] = useState('ALL');

    const [organizersRange, setOrganizersRange] = useState('ALL');
    const [organizersYear, setOrganizersYear] = useState('2026');
    const [organizersMonth, setOrganizersMonth] = useState('01');
    const [organizersWeekStartDate, setOrganizersWeekStartDate] = useState(DEFAULT_WEEK_RANGE.start);
    const [organizersWeekEndDate, setOrganizersWeekEndDate] = useState(DEFAULT_WEEK_RANGE.end);

    const [eventsRange, setEventsRange] = useState('ALL');
    const [eventsYear, setEventsYear] = useState('2026');
    const [eventsMonth, setEventsMonth] = useState('01');
    const [eventsWeekStartDate, setEventsWeekStartDate] = useState(DEFAULT_WEEK_RANGE.start);
    const [eventsWeekEndDate, setEventsWeekEndDate] = useState(DEFAULT_WEEK_RANGE.end);
    const [eventsSummaryMode, setEventsSummaryMode] = useState('ALL');
    const [eventsMetric, setEventsMetric] = useState('views');
    const [eventsCategory, setEventsCategory] = useState('0');
    const [rankingRange, setRankingRange] = useState('ALL_TIME');
    const [rankingCategory, setRankingCategory] = useState('0');
    const [rankingCategories, setRankingCategories] = useState([{ value: '0', label: 'Wszystkie', apiValue: '0' }]);
    const [rankingVisibleList, setRankingVisibleList] = useState('interest');
    const [rankingInfoModalOpen, setRankingInfoModalOpen] = useState(false);
    const [rankingInterest, setRankingInterest] = useState([]);
    const [rankingPacks, setRankingPacks] = useState([]);
    const [eventsTopInterestApi, setEventsTopInterestApi] = useState(null);
    const [eventsTopPacksApi, setEventsTopPacksApi] = useState(null);
    const [eventsTopInterestExpanded, setEventsTopInterestExpanded] = useState(false);
    const [eventsTopPacksExpanded, setEventsTopPacksExpanded] = useState(false);
    const [packSeriesApi, setPackSeriesApi] = useState(null);
    const [usersSeriesApi, setUsersSeriesApi] = useState({});
    const [usersSummaryApi, setUsersSummaryApi] = useState(null);
    const [usersDemographicsApi, setUsersDemographicsApi] = useState(null);
    const [organizersSeriesApi, setOrganizersSeriesApi] = useState({});
    const [organizersSummaryApi, setOrganizersSummaryApi] = useState(null);
    const [eventsSummaryApi, setEventsSummaryApi] = useState(null);
    const [eventsCategoriesApi, setEventsCategoriesApi] = useState(null);
    const [eventsAvgParticipantsSeriesApi, setEventsAvgParticipantsSeriesApi] = useState(null);
    const [eventStatsSearch, setEventStatsSearch] = useState('');
    const [eventStatsList, setEventStatsList] = useState([]);
    const [eventStatsListLoading, setEventStatsListLoading] = useState(false);
    const [eventStatsListLoadingMore, setEventStatsListLoadingMore] = useState(false);
    const [eventStatsListError, setEventStatsListError] = useState('');
    const [selectedEventStatsId, setSelectedEventStatsId] = useState(null);
    const [eventViewingSeriesApi, setEventViewingSeriesApi] = useState(null);
    const [eventPackSeriesApi, setEventPackSeriesApi] = useState(null);
    const [eventActivitySeriesApi, setEventActivitySeriesApi] = useState(null);
    const [eventSummaryApi, setEventSummaryApi] = useState(null);
    const [eventAgeDataApi, setEventAgeDataApi] = useState(null);
    const [eventImageUrlById, setEventImageUrlById] = useState({});
    const [rankingEventMetaById, setRankingEventMetaById] = useState({});
    const [eventAgeFilter, setEventAgeFilter] = useState('ALL');
    const [eventVisibleSeries, setEventVisibleSeries] = useState({
        viewing: true,
        packs: true,
        favorites: true,
    });
    const [eventInfoModalKey, setEventInfoModalKey] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const eventInfoModalContent = EVENT_INFO_CONTENT[eventInfoModalKey] || null;
    const fetchedImageHashesByEventRef = useRef(new Map());
    const objectUrlByEventRef = useRef(new Map());
    const imageFetchInFlightByEventRef = useRef(new Set());
    const failedRankingMetaByEventRef = useRef(new Map());
    const organizerNameByOwnerIdRef = useRef(new Map());
    const organizerNameFetchInFlightRef = useRef(new Set());
    const organizerNameFailedOwnerIdsRef = useRef(new Map());
    const rankingMetaFetchInFlightRef = useRef(new Set());
    const eventDetailFetchInFlightRef = useRef(new Set());
    const eventDetailFetchFailedRef = useRef(new Map());
    const eventStatsPageFetchInFlightRef = useRef(new Set());
    const isInRetryCooldown = useCallback((failedRef, key) => {
        const failedAt = failedRef.current.get(key);
        if (!failedAt) return false;
        return (Date.now() - failedAt) < RETRY_COOLDOWN_MS;
    }, []);

    const patchOrganizerNameByOwnerId = useCallback((ownerId, organizerName) => {
        const ownerIdKey = normalizeOwnerIdKey(ownerId);
        if (!ownerIdKey) return;

        const resolvedOrganizerName = String(organizerName || '').trim() || ORGANIZER_NAME_FALLBACK;
        organizerNameByOwnerIdRef.current.set(ownerIdKey, resolvedOrganizerName);

        setEventStatsList((previous) => previous.map((item) => (
            (
                (resolveOwnerIdFromEventLike(item) || resolveOwnerIdFromEventLike(item?.statsPayload)) === ownerIdKey
                && !hasOrganizerName(item?.organizerName)
            )
                ? { ...item, ownerId: ownerIdKey, organizerName: resolvedOrganizerName }
                : item
        )));

    }, []);

    const fetchOrganizerCompanyNameByOwnerId = useCallback(async (ownerId) => {
        const ownerIdKey = normalizeOwnerIdKey(ownerId);
        if (!ownerIdKey || !accessToken) return ORGANIZER_NAME_FALLBACK;

        try {
            const response = await fetch(`/api/organiser/profiles/${ownerIdKey}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                credentials: 'include',
                mode: 'cors',
            });

            if (response.status === 404) return ORGANIZER_NAME_FALLBACK;
            if (!response.ok) return ORGANIZER_NAME_FALLBACK;

            const payload = mapApiUtcToPolishTime(await response.json().catch(() => null));
            return String(payload?.companyName || '').trim() || ORGANIZER_NAME_FALLBACK;
        } catch (error) {
            return ORGANIZER_NAME_FALLBACK;
        }
    }, [accessToken]);
    const ensureOrganizerNameForEventItem = useCallback((eventItem) => {
        if (!eventItem) return eventItem;

        const ownerIdKey = resolveOwnerIdFromEventLike(eventItem) || resolveOwnerIdFromEventLike(eventItem?.statsPayload);
        if (!ownerIdKey || hasOrganizerName(eventItem?.organizerName)) {
            return eventItem;
        }

        const cachedOrganizerName = organizerNameByOwnerIdRef.current.get(ownerIdKey);
        if (cachedOrganizerName) {
            return {
                ...eventItem,
                ownerId: ownerIdKey,
                organizerName: cachedOrganizerName,
            };
        }
        if (isInRetryCooldown(organizerNameFailedOwnerIdsRef, ownerIdKey)) {
            return eventItem;
        }

        if (!organizerNameFetchInFlightRef.current.has(ownerIdKey)) {
            organizerNameFetchInFlightRef.current.add(ownerIdKey);
            fetchOrganizerCompanyNameByOwnerId(ownerIdKey)
                .then((companyName) => {
                    if (!hasOrganizerName(companyName)) {
                        organizerNameFailedOwnerIdsRef.current.set(ownerIdKey, Date.now());
                        return;
                    }
                    organizerNameFailedOwnerIdsRef.current.delete(ownerIdKey);
                    patchOrganizerNameByOwnerId(ownerIdKey, companyName);
                })
                .finally(() => {
                    organizerNameFetchInFlightRef.current.delete(ownerIdKey);
                });
        }

        return eventItem;
    }, [fetchOrganizerCompanyNameByOwnerId, isInRetryCooldown, patchOrganizerNameByOwnerId]);

    const handleSidebarClick = (tabName) => {
        dispatch(toggleActiveTab(tabName));
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            setRefreshTick((previous) => previous + 1);
        }, 60000);

        return () => {
            clearInterval(intervalId);
        };
    }, []);
    const handleEventVisibleSeriesToggle = useCallback((seriesKey) => {
        setEventVisibleSeries((previous) => {
            const activeCount = Object.values(previous).filter(Boolean).length;
            if (previous[seriesKey] && activeCount === 1) return previous;

            return {
                ...previous,
                [seriesKey]: !previous[seriesKey],
            };
        });
    }, []);
    const handleEventTimeScopeChange = useCallback((event) => {
        const nextValue = event.target.value;
        if (nextValue === 'ALL') {
            setEventsRange('ALL');
            return;
        }

        const currentWeekRange = getCurrentWeekRangeValues();
        setEventsRange('WEEK');
        setEventsWeekStartDate(currentWeekRange.start);
        setEventsWeekEndDate(currentWeekRange.end);
    }, []);

    const requestedEventContext = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const view = searchParams.get('view');
        const parsedEventId = normalizeEventIdKey(searchParams.get('eventId'));

        return {
            isEventView: view === 'event',
            eventId: parsedEventId,
        };
    }, [location.search]);

    const handleSelectEventStats = useCallback((eventItem) => {
        const eventId = normalizeEventIdKey(eventItem?.id);
        if (!eventId) return;

        const resolvedEventItem = ensureOrganizerNameForEventItem(eventItem);

        setSelectedEventStatsId(eventId);
        dispatch(toggleActiveTab(EVENT_DETAIL_TAB));
        navigate(`/statistics?view=event&eventId=${eventId}`, {
            replace: true,
            state: { eventFromList: resolvedEventItem },
        });
    }, [dispatch, navigate, ensureOrganizerNameForEventItem]);

    const categoryOptionsByValue = useMemo(
        () => new Map((rankingCategories || []).map((item) => [String(item.value), item])),
        [rankingCategories]
    );
    const resolveCategoryApiValue = useCallback((selectedValue) => {
        if (selectedValue == null) return '0';

        const option = categoryOptionsByValue.get(String(selectedValue));
        const numericFromOption = resolveNumericCategoryId(option?.apiValue, option?.value);
        if (numericFromOption) return numericFromOption;

        return resolveNumericCategoryId(selectedValue) || '0';
    }, [categoryOptionsByValue]);

    const renderRangeFilters = ({
        range,
        setRange,
        year,
        setYear,
        month,
        weekStartDate,
        setWeekStartDate,
        weekEndDate,
        setWeekEndDate,
    }) => {
        const dateRange = isCalendarRange(range)
            ? getRangeDates(range, year, month, weekStartDate, weekEndDate)
            : null;

        return (
            <div className="stats-filter-group">
                <CustomDropdown
                    placeholder="Zakres"
                    options={RANGE_OPTIONS}
                    value={range}
                    onChange={(event) => setRange(event.target.value)}
                    hoverClass="hover-active"
                    activeClass="is-active"
                />
                {range === 'YEAR' && (
                    <CustomDropdown
                        placeholder="Rok"
                        options={YEAR_OPTIONS}
                        value={year}
                        onChange={(event) => setYear(event.target.value)}
                        hoverClass="hover-active"
                        activeClass="is-active"
                    />
                )}
                {isCalendarRange(range) && (
                    <>
                        <DateRangePicker
                            startValue={weekStartDate}
                            endValue={weekEndDate}
                            monthOptions={MONTH_OPTIONS}
                            parseDateValue={parseDateValue}
                            normalizeDateRangeValues={normalizeDateRangeValues}
                            formatDatePl={formatDatePl}
                            toDateInputValue={toDateInputValue}
                            toUtcDayStamp={toUtcDayStamp}
                            isSameUtcDate={isSameUtcDate}
                            onChange={(nextStartDate, nextEndDate) => {
                                setWeekStartDate(nextStartDate);
                                setWeekEndDate(nextEndDate);
                            }}
                        />
                        <div className="stats-week-range-caption">
                            Zakres: {formatDatePl(dateRange.from)} - {formatDatePl(dateRange.to)}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const mapRankingData = useCallback((data, metricType, options = {}) => {
        const { limit = RANKING_LIST_LIMIT } = options;
        const list = extractCollectionPayload(data);
        if (!list.length) return [];

        const mapped = list.map((item, index) => {
            const locationValue = String(item?.location || '').trim();
            const [locationPlace, locationCity] = locationValue
                ? locationValue.split(',').map((part) => String(part || '').trim())
                : ['', ''];
            const metricTypeUpper = String(metricType || '').toUpperCase();
            const metricValue = metricTypeUpper === 'VIEWS'
                ? pickFirstMetricNumber(item?.metricValue, item?.value, item?.views, item?.viewCount, item?.viewingCount, item?.totalViews, item?.count)
                : metricTypeUpper === 'LIKES'
                    ? pickFirstMetricNumber(item?.metricValue, item?.value, item?.likes, item?.likesCount, item?.likeCount, item?.count)
                    : pickFirstMetricNumber(item?.metricValue, item?.value, item?.packs, item?.packCount, item?.bunchCount, item?.bunchesCount, item?.createdBunches, item?.count);
            const imageHashCode = extractEventImageHash(item);
            const eventId = item?.eventId ?? item?.id ?? index + 1;
            const resolvedTitle = item?.title ?? item?.name ?? item?.eventName ?? '';

            return {
                id: eventId,
                eventId,
                title: resolvedTitle,
                name: resolvedTitle,
                city: (item?.city ?? item?.eventCity ?? locationCity) || '',
                place: (item?.place ?? item?.eventPlace ?? locationPlace) || '',
                location: locationValue,
                startDateRaw: item?.startDate || item?.date || item?.meetingDate || item?.added_on_utc || null,
                date: formatRankingDate(item?.startDate || item?.date || item?.meetingDate || item?.added_on_utc),
                time: formatRankingTime(item?.startDate || item?.date || item?.meetingDate || item?.added_on_utc),
                rank: index + 1,
                value: metricValue,
                metricValue,
                imageHashCode,
            };
        });

        return typeof limit === 'number' ? mapped.slice(0, limit) : mapped;
    }, []);

    const handleOpenEventFromStats = useCallback(async (eventItem) => {
        const eventId = normalizeEventIdKey(eventItem?.id ?? eventItem?.eventId);
        if (!eventId) {
            return;
        }

        navigate('/events');

        try {
            const fullEventData = mapStatsEventToDetailPanelEvent({
                ...(eventItem || {}),
                id: eventId,
                eventId,
            });
            dispatch(setEditedEvent(fullEventData));
        } catch (error) {
            console.error(`Błąd pobierania szczegółów wydarzenia ${eventId}:`, error);
        }
    }, [dispatch, navigate]);

    const fetchRankings = useCallback(async () => {
        try {
            const rankingCategoryApiValue = resolveCategoryApiValue(rankingCategory);
            const [interestResponse, packsResponse] = await Promise.all([
                ApiService.getStatisticsRankingsEvents({
                    metric: 'LIKES',
                    timeRange: rankingRange,
                    limit: RANKING_LIST_LIMIT,
                    ...(rankingCategoryApiValue !== '0' && { category: rankingCategoryApiValue }),
                }),
                ApiService.getStatisticsRankingsEvents({
                    metric: 'JOINS',
                    timeRange: rankingRange,
                    limit: RANKING_LIST_LIMIT,
                    ...(rankingCategoryApiValue !== '0' && { category: rankingCategoryApiValue }),
                }),
            ]);

            const interestMapped = mapUnifiedRankingItems(interestResponse, 'LIKES');
            const packsMapped = mapUnifiedRankingItems(packsResponse, 'JOINS');

            updateStateIfChanged(setRankingInterest, interestMapped);
            updateStateIfChanged(setRankingPacks, packsMapped);
        } catch (error) {
            console.error('Błąd pobierania rankingów:', error);
            updateStateIfChanged(setRankingInterest, []);
            updateStateIfChanged(setRankingPacks, []);
        }
    }, [rankingRange, rankingCategory, resolveCategoryApiValue]);

    useEffect(() => {
        let isMounted = true;

        ApiService.getEventsCategories()
            .then((response) => {
                const data = response?.data || response || [];
                if (!Array.isArray(data) || !isMounted) return;

                const mappedCategories = data
                    .map((item) => {
                        const categoryId = resolveNumericCategoryId(item?.id);
                        if (!categoryId) return null;

                        return {
                            value: categoryId,
                            label: String(item?.name ?? item?.nameEng ?? `Kategoria ${categoryId}`),
                            apiValue: categoryId,
                        };
                    })
                    .filter(Boolean);

                setRankingCategories([
                    { value: '0', label: 'Wszystkie', apiValue: '0' },
                    ...mappedCategories,
                ]);
            })
            .catch((error) => {
                console.error('Błąd pobierania kategorii:', error);
                if (!isMounted) return;
                setRankingCategories([{ value: '0', label: 'Wszystkie', apiValue: '0' }]);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const hasCurrentRankingCategory = rankingCategories.some((item) => String(item.value) === String(rankingCategory));
        if (!hasCurrentRankingCategory) {
            setRankingCategory('0');
        }
    }, [rankingCategories, rankingCategory]);

    useEffect(() => {
        const hasCurrentEventsCategory = rankingCategories.some((item) => String(item.value) === String(eventsCategory));
        if (!hasCurrentEventsCategory) {
            setEventsCategory('0');
        }
    }, [rankingCategories, eventsCategory]);

    useEffect(() => {
        if (activeTab !== 'Rankingi') return;
        fetchRankings();
    }, [activeTab, fetchRankings, refreshTick]);
    useEffect(() => {
        if (activeTab !== 'Rankingi') return;

        const candidateIds = Array.from(new Set(
            [...(rankingInterest || []), ...(rankingPacks || [])]
                .map((item) => normalizeEventIdKey(item?.id ?? item?.eventId))
                .filter(Boolean)
        ));

        candidateIds.forEach((eventId) => {
            if (rankingEventMetaById[eventId]) return;
            if (rankingMetaFetchInFlightRef.current.has(eventId)) return;
            if (isInRetryCooldown(failedRankingMetaByEventRef, eventId)) return;
            if (isUuidLike(eventId)) return;

            rankingMetaFetchInFlightRef.current.add(eventId);
            ApiService.getEvent(eventId)
                .then((response) => {
                    const payload = response?.data || response || {};
                    const mapped = mapRankingEventMetaFromApi(payload);
                    const hasAnyMeta = Boolean(
                        String(mapped?.name || '').trim()
                        || String(mapped?.place || '').trim()
                        || String(mapped?.city || '').trim()
                        || mapped?.startDateRaw
                    );
                    if (!hasAnyMeta) {
                        failedRankingMetaByEventRef.current.set(eventId, Date.now());
                        return;
                    }
                    failedRankingMetaByEventRef.current.delete(eventId);
                    setRankingEventMetaById((previous) => ({
                        ...previous,
                        [eventId]: mapped,
                    }));
                })
                .catch((error) => {
                    console.error(`Błąd pobierania metadanych wydarzenia ${eventId}:`, error);
                    failedRankingMetaByEventRef.current.set(eventId, Date.now());
                })
                .finally(() => {
                    rankingMetaFetchInFlightRef.current.delete(eventId);
                });
        });
    }, [activeTab, isInRetryCooldown, rankingInterest, rankingPacks, rankingEventMetaById]);

    useEffect(() => {
        if (activeTab !== 'Rankingi') {
            setRankingInfoModalOpen(false);
        }
    }, [activeTab]);

    useEffect(() => {
        setRankingInfoModalOpen(false);
    }, [rankingVisibleList]);

    useEffect(() => {
        if (!rankingInfoModalOpen) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setRankingInfoModalOpen(false);
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => {
            window.removeEventListener('keydown', handleEscape);
        };
    }, [rankingInfoModalOpen]);

    useEffect(() => {
        if (activeTab !== 'Użytkownicy') return;

        const query = buildStatisticsQuery(usersRange, usersYear, usersMonth, usersWeekStartDate, usersWeekEndDate);
        const usersScopeParams = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
        };
        let isMounted = true;

        Promise.allSettled([
            ApiService.getStatisticsSummary(usersScopeParams),
            ApiService.getStatisticsDemographics(usersScopeParams),
            ApiService.getStatisticsSeries('REGISTERED', query),
            ApiService.getStatisticsSeries('NEW', query),
            ApiService.getStatisticsSeries('ACTIVE', query),
            ApiService.getStatisticsSeries('PACKAGES', query),
            ApiService.getStatisticsSeries('EVENT_PARTICIPATION', query),
            ApiService.getStatisticsSeries('DELETED', query),
            ApiService.getStatisticsSeries('REPORTS', query),
        ])
            .then(([
                summaryResult,
                demographicsResult,
                registeredResult,
                newResult,
                activeResult,
                packagesResult,
                eventParticipationResult,
                deletedResult,
                reportsResult,
            ]) => {
                if (!isMounted) return;

                const mapSeries = (result) => (
                    result.status === 'fulfilled'
                        ? buildSeriesFromResponse(extractTimeSeriesPayload(result.value), usersRange, {
                            year: usersYear,
                            month: usersMonth,
                            startValue: usersWeekStartDate,
                            endValue: usersWeekEndDate,
                        })
                        : null
                );
                const mapDemography = (result) => {
                    if (result.status !== 'fulfilled') return null;
                    const payload = extractDemographicBreakdownPayload(result.value);
                    if (!payload) return null;

                    return {
                        gender: normalizeDemographicGender(payload?.gender || {}),
                        ages: normalizeDemographicAges(payload?.ageGroups || payload?.ages || {}),
                    };
                };

                const nextUsersSummaryApi = summaryResult.status === 'fulfilled' ? summaryResult.value : null;
                const nextUsersDemographicsApi = demographicsResult.status === 'fulfilled' ? demographicsResult.value : null;
                const nextUsersSeriesApi = {
                    registered: mapSeries(registeredResult),
                    newUsers: mapSeries(newResult),
                    activeUsers: mapSeries(activeResult),
                    packActivity: mapSeries(packagesResult),
                    confirmedOutings: mapSeries(eventParticipationResult),
                    accountDeletions: mapSeries(deletedResult),
                    reports: mapSeries(reportsResult),
                    demography: {
                        registered: mapDemography(registeredResult),
                        newUsers: mapDemography(newResult),
                        activeUsers: mapDemography(activeResult),
                        packActivity: mapDemography(packagesResult),
                        confirmedOutings: mapDemography(eventParticipationResult),
                        accountDeletions: mapDemography(deletedResult),
                        reports: mapDemography(reportsResult),
                    },
                };
                const nextPackSeriesApi = mapSeries(packagesResult);

                updateStateIfChanged(setUsersSummaryApi, nextUsersSummaryApi);
                updateStateIfChanged(setUsersDemographicsApi, nextUsersDemographicsApi);
                updateStateIfChanged(setUsersSeriesApi, nextUsersSeriesApi);
                updateStateIfChanged(setPackSeriesApi, nextPackSeriesApi);
            })
            .catch((error) => {
                console.error('Błąd pobierania statystyk paczek:', error);
                updateStateIfChanged(setPackSeriesApi, null);
                updateStateIfChanged(setUsersSeriesApi, {});
                updateStateIfChanged(setUsersSummaryApi, null);
                updateStateIfChanged(setUsersDemographicsApi, null);
            });

        return () => {
            isMounted = false;
        };
    }, [activeTab, usersRange, usersYear, usersMonth, usersWeekStartDate, usersWeekEndDate, refreshTick]);

    useEffect(() => {
        if (activeTab !== 'Organizatorzy') return;

        const query = buildStatisticsQuery(
            organizersRange,
            organizersYear,
            organizersMonth,
            organizersWeekStartDate,
            organizersWeekEndDate
        );
        const organizersScopeParams = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
        };
        let isMounted = true;

        Promise.allSettled([
            ApiService.getStatisticsB2BSummary(organizersScopeParams),
            ApiService.getStatisticsB2BSeries('TOTAL_ORGANIZERS', query),
            ApiService.getStatisticsB2BSeries('NEW_ORGANIZERS', query),
        ])
            .then(([summaryResult, totalResult, newResult]) => {
                if (!isMounted) return;

                const mapSeries = (result) => (
                    result.status === 'fulfilled'
                        ? buildSeriesFromResponse(
                            extractTimeSeriesPayload(result.value),
                            organizersRange,
                            {
                                year: organizersYear,
                                month: organizersMonth,
                                startValue: organizersWeekStartDate,
                                endValue: organizersWeekEndDate,
                            }
                        )
                        : null
                );

                const nextOrganizersSummaryApi = summaryResult.status === 'fulfilled' ? summaryResult.value : null;
                const nextOrganizersSeriesApi = {
                    registered: mapSeries(totalResult),
                    newOrganizers: mapSeries(newResult),
                };

                updateStateIfChanged(setOrganizersSummaryApi, nextOrganizersSummaryApi);
                updateStateIfChanged(setOrganizersSeriesApi, nextOrganizersSeriesApi);
            })
            .catch((error) => {
                console.error('Błąd pobierania statystyk B2B:', error);
                updateStateIfChanged(setOrganizersSummaryApi, null);
                updateStateIfChanged(setOrganizersSeriesApi, {});
            });

        return () => {
            isMounted = false;
        };
    }, [activeTab, organizersRange, organizersYear, organizersMonth, organizersWeekStartDate, organizersWeekEndDate, refreshTick]);

    useEffect(() => {
        if (activeTab !== 'Wydarzenia') return;

        const status = eventsSummaryMode === 'ACTIVE' ? 'ACTIVE' : 'ALL';
        const summaryQuery = buildStatisticsQuery(
            eventsSummaryMode === 'ACTIVE' ? 'ALL' : eventsRange,
            eventsYear,
            eventsMonth,
            eventsWeekStartDate,
            eventsWeekEndDate
        );
        const selectedCategoryId = resolveCategoryApiValue(eventsCategory);
        const params = {
            dateFrom: summaryQuery.dateFrom,
            dateTo: summaryQuery.dateTo,
            status,
            ...(selectedCategoryId !== '0' && { category: selectedCategoryId }),
        };
        const seriesParams = {
            ...params,
            interval: summaryQuery.interval,
        };
        let isMounted = true;

        Promise.allSettled([
            ApiService.getStatisticsEventsSummary(params),
            ApiService.getStatisticsEventsAverageParticipantsSeries(seriesParams),
        ])
            .then(([summaryResult, avgSeriesResult]) => {
                if (!isMounted) return;

                const nextEventsSummaryApi = summaryResult.status === 'fulfilled' ? summaryResult.value : null;
                const nextEventsAvgParticipantsSeriesApi = avgSeriesResult.status === 'fulfilled'
                    ? buildAverageParticipantsSeriesFromResponse(avgSeriesResult.value)
                    : null;

                updateStateIfChanged(setEventsSummaryApi, nextEventsSummaryApi);
                updateStateIfChanged(setEventsAvgParticipantsSeriesApi, nextEventsAvgParticipantsSeriesApi);
            })
            .catch((error) => {
                console.error('Błąd pobierania statystyk wydarzeń:', error);
                updateStateIfChanged(setEventsSummaryApi, null);
                updateStateIfChanged(setEventsAvgParticipantsSeriesApi, null);
            });

        return () => {
            isMounted = false;
        };
    }, [
        activeTab,
        eventsSummaryMode,
        eventsRange,
        eventsYear,
        eventsMonth,
        eventsWeekStartDate,
        eventsWeekEndDate,
        eventsCategory,
        resolveCategoryApiValue,
        refreshTick,
    ]);

    useEffect(() => {
        if (activeTab !== 'Wydarzenia') return;

        const query = buildStatisticsQuery(eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate);
        const params = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
        };
        let isMounted = true;

        ApiService.getStatisticsEventsCategories(params)
            .then((response) => {
                if (!isMounted) return;
                updateStateIfChanged(setEventsCategoriesApi, response || null);
            })
            .catch((error) => {
                console.error('Błąd pobierania kategorii wydarzeń:', error);
                if (!isMounted) return;
                updateStateIfChanged(setEventsCategoriesApi, null);
            });

        return () => {
            isMounted = false;
        };
    }, [
        activeTab,
        eventsRange,
        eventsYear,
        eventsMonth,
        eventsWeekStartDate,
        eventsWeekEndDate,
        refreshTick,
    ]);

    useEffect(() => {
        if (activeTab !== 'Wydarzenia') return;

        const selectedEventType = resolveCategoryApiValue(eventsCategory);
        const query = buildStatisticsQuery(eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate);
        const rankingParams = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
            limit: 50,
            ...(selectedEventType !== '0' && { eventType: selectedEventType }),
        };

        Promise.all([
            ApiService.getStatisticsEventsRanking('VIEWS', rankingParams),
            ApiService.getStatisticsEventsRanking('LIKES', rankingParams),
            ApiService.getStatisticsEventsRanking('PACKAGES', rankingParams),
        ])
            .then(([viewsResponse, likesResponse, packsResponse]) => {
                const viewsMapped = mapRankingData(viewsResponse, 'VIEWS', { limit: null });
                const likesMapped = mapRankingData(likesResponse, 'LIKES', { limit: null });
                const packsMapped = mapRankingData(packsResponse, 'PACKAGES', { limit: null });

                const byId = new Map();
                viewsMapped.forEach((item) => {
                    const idKey = normalizeEventIdKey(item.id);
                    if (!idKey) return;
                    byId.set(idKey, {
                        ...item,
                        views: toSafeMetricNumber(item?.metricValue) ?? toSafeMetricNumber(item?.value),
                    });
                });
                likesMapped.forEach((item) => {
                    const idKey = normalizeEventIdKey(item.id);
                    if (!idKey) return;
                    const previous = byId.get(idKey) || item;
                    byId.set(idKey, {
                        ...previous,
                        likes: toSafeMetricNumber(item?.metricValue) ?? toSafeMetricNumber(item?.value),
                    });
                });
                packsMapped.forEach((item) => {
                    const idKey = normalizeEventIdKey(item.id);
                    if (!idKey) return;
                    const previous = byId.get(idKey) || item;
                    byId.set(idKey, {
                        ...previous,
                        packs: toSafeMetricNumber(item?.metricValue) ?? toSafeMetricNumber(item?.value),
                    });
                });

                const merged = Array.from(byId.values());
                updateStateIfChanged(setEventsTopInterestApi, merged.length ? merged : null);
                updateStateIfChanged(setEventsTopPacksApi, packsMapped.length ? packsMapped : null);
            })
            .catch((error) => {
                console.error('Błąd pobierania statystyk top wydarzeń:', error);
                updateStateIfChanged(setEventsTopInterestApi, null);
                updateStateIfChanged(setEventsTopPacksApi, null);
            });
    }, [activeTab, eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate, eventsCategory, mapRankingData, resolveCategoryApiValue, refreshTick]);

    useEffect(() => {
        setEventsTopInterestExpanded(false);
        setEventsTopPacksExpanded(false);
    }, [eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate, eventsMetric, eventsCategory]);

    useEffect(() => {
        if (!requestedEventContext.isEventView) return;

        dispatch(toggleActiveTab(EVENT_DETAIL_TAB));
        if (requestedEventContext.eventId != null) {
            setSelectedEventStatsId(requestedEventContext.eventId);
        }
    }, [requestedEventContext, dispatch]);

    useEffect(() => {
        const eventFromState = ensureOrganizerNameForEventItem(mapStatsEventToSidebarItem(location.state?.eventFromList));
        if (!eventFromState) return;

        setEventStatsList((previous) => upsertStatsEvent(previous, eventFromState));

        if (!selectedEventStatsId || normalizeEventIdKey(selectedEventStatsId) !== normalizeEventIdKey(eventFromState.id)) {
            setSelectedEventStatsId(eventFromState.id);
        }
    }, [location.state, selectedEventStatsId, ensureOrganizerNameForEventItem]);
    const loadEventStatsPage = useCallback(async ({ searchText = '', signal } = {}) => {
        const normalizedSearch = String(searchText || '').trim();
        const requestKey = normalizedSearch;

        if (signal?.aborted) return;
        if (eventStatsPageFetchInFlightRef.current.has(requestKey)) return;
        eventStatsPageFetchInFlightRef.current.add(requestKey);

        setEventStatsListLoading(true);
        setEventStatsListLoadingMore(false);
        setEventStatsList([]);
        setEventStatsListError('');

        try {
            const response = await ApiService.searchEventsForAdminV2(normalizedSearch, { signal });

            if (signal?.aborted) return;

            const rawEvents = Array.isArray(response)
                ? response
                : (Array.isArray(response?.data)
                    ? response.data
                    : (Array.isArray(response?.content) ? response.content : []));
            const mapped = rawEvents
                .map((item, index) => mapStatsEventToSidebarItem(item, index))
                .map((item) => ensureOrganizerNameForEventItem(item))
                .filter(Boolean);

            setEventStatsList(mapped);
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || signal?.aborted) return;
            console.error('Błąd pobierania listy wydarzeń do statystyk:', error);
            setEventStatsListError('Nie udało się pobrać listy wydarzeń.');
        } finally {
            eventStatsPageFetchInFlightRef.current.delete(requestKey);
            if (signal?.aborted) return;
            setEventStatsListLoading(false);
            setEventStatsListLoadingMore(false);
        }
    }, [ensureOrganizerNameForEventItem]);

    useEffect(() => {
        if (activeTab !== EVENT_DETAIL_TAB) return undefined;

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => {
            loadEventStatsPage({
                searchText: eventStatsSearch,
                signal: controller.signal,
            });
        }, 250);

        return () => {
            window.clearTimeout(timeoutId);
            controller.abort();
        };
    }, [activeTab, eventStatsSearch, loadEventStatsPage]);

    useEffect(() => {
        const ownersToResolve = Array.from(
            new Set(
                (eventStatsList || [])
                    .map((item) => ({
                        ownerId: resolveOwnerIdFromEventLike(item) || resolveOwnerIdFromEventLike(item?.statsPayload),
                        organizerName: item?.organizerName,
                    }))
                    .filter(({ ownerId, organizerName }) => (
                        Boolean(ownerId)
                        && !hasOrganizerName(organizerName)
                        && !organizerNameByOwnerIdRef.current.has(ownerId)
                        && !isInRetryCooldown(organizerNameFailedOwnerIdsRef, ownerId)
                        && !organizerNameFetchInFlightRef.current.has(ownerId)
                    ))
                    .map(({ ownerId }) => ownerId)
            )
        );

        if (!ownersToResolve.length) return;

        ownersToResolve.forEach((ownerId) => {
            organizerNameFetchInFlightRef.current.add(ownerId);
            fetchOrganizerCompanyNameByOwnerId(ownerId)
                .then((companyName) => {
                    if (!hasOrganizerName(companyName)) {
                        organizerNameFailedOwnerIdsRef.current.set(ownerId, Date.now());
                        return;
                    }
                    organizerNameFailedOwnerIdsRef.current.delete(ownerId);
                    patchOrganizerNameByOwnerId(ownerId, companyName);
                })
                .finally(() => {
                    organizerNameFetchInFlightRef.current.delete(ownerId);
                });
        });
    }, [eventStatsList, fetchOrganizerCompanyNameByOwnerId, isInRetryCooldown, patchOrganizerNameByOwnerId]);

    useEffect(() => {
        if (!eventStatsList.length) return;

        if (selectedEventStatsId != null && eventStatsList.some((item) => normalizeEventIdKey(item.id) === normalizeEventIdKey(selectedEventStatsId))) {
            return;
        }

        if (requestedEventContext.eventId != null) {
            const requestedExists = eventStatsList.some((item) => normalizeEventIdKey(item.id) === normalizeEventIdKey(requestedEventContext.eventId));
            if (requestedExists) {
                setSelectedEventStatsId(requestedEventContext.eventId);
                return;
            }
        }

        setSelectedEventStatsId(eventStatsList[0].id);
    }, [eventStatsList, selectedEventStatsId, requestedEventContext.eventId]);

    useEffect(() => {
        if (selectedEventStatsId == null) return;
        if (isUuidLike(selectedEventStatsId)) return;

        let isMounted = true;

        ApiService.getEvent(selectedEventStatsId)
            .then((response) => {
                if (!isMounted) return;

                const mapped = ensureOrganizerNameForEventItem(mapStatsEventToSidebarItem(response?.data || {}));
                if (!mapped) return;

                if (mapped.ownerId && hasOrganizerName(mapped.organizerName)) {
                    organizerNameByOwnerIdRef.current.set(mapped.ownerId, mapped.organizerName);
                }

                setEventStatsList((previous) => upsertStatsEvent(previous, mapped));
            })
            .catch((error) => {
                console.error(`Błąd pobierania szczegółów wydarzenia ${selectedEventStatsId}:`, error);
            });

        return () => {
            isMounted = false;
        };
    }, [selectedEventStatsId, ensureOrganizerNameForEventItem]);

    useEffect(() => {
        if (activeTab !== EVENT_DETAIL_TAB || selectedEventStatsId == null) {
            return;
        }

        const query = buildStatisticsQuery(eventsRange, eventsYear, eventsMonth, eventsWeekStartDate, eventsWeekEndDate);
        const params = {
            dateFrom: query.dateFrom,
            dateTo: query.dateTo,
        };
        let isMounted = true;

        Promise.allSettled([
            ApiService.getStatisticsEventSummary(selectedEventStatsId, params),
            ApiService.getStatisticsEventDemographicsAge(selectedEventStatsId, params),
            ApiService.getStatisticsEventActivitySeries(selectedEventStatsId, {
                ...params,
                interval: query.interval,
            }),
        ])
            .then(([summaryResult, ageResult, activityResult]) => {
                if (!isMounted) return;

                updateStateIfChanged(setEventSummaryApi, summaryResult.status === 'fulfilled' ? summaryResult.value : null);

                if (ageResult.status === 'fulfilled') {
                    const ageResponse = ageResult.value || {};
                    const ageGroups = Array.isArray(ageResponse?.ageGroups) ? ageResponse.ageGroups : [];
                    const ageDataByGender = ageResponse?.data && typeof ageResponse.data === 'object'
                        ? ageResponse.data
                        : null;

                    let mappedAge = [];
                    if (ageDataByGender && ageGroups.length) {
                        mappedAge = ageGroups.map((ageRange) => {
                            const key = String(ageRange || '').replace('–', '-');
                            const women = toSafeMetricNumber(ageDataByGender?.K?.[ageRange] ?? ageDataByGender?.K?.[key]) ?? 0;
                            const men = toSafeMetricNumber(ageDataByGender?.M?.[ageRange] ?? ageDataByGender?.M?.[key]) ?? 0;
                            const other = toSafeMetricNumber(ageDataByGender?.I?.[ageRange] ?? ageDataByGender?.I?.[key]) ?? 0;
                            return {
                                range: key,
                                count: clampValue(women + men + other),
                                byGender: {
                                    K: clampValue(women),
                                    M: clampValue(men),
                                    I: clampValue(other),
                                },
                            };
                        });
                    } else {
                        const rawAge = extractCollectionPayload(ageResponse);
                        mappedAge = (Array.isArray(rawAge) ? rawAge : []).map((item) => ({
                            range: String(item?.range || item?.ageRange || item?.label || '').replace('–', '-'),
                            count: clampValue(item?.count ?? item?.value ?? 0),
                            byGender: {
                                K: clampValue(item?.K ?? item?.women ?? 0),
                                M: clampValue(item?.M ?? item?.men ?? 0),
                                I: clampValue(item?.I ?? item?.other ?? 0),
                            },
                        }));
                    }

                    updateStateIfChanged(setEventAgeDataApi, mappedAge.length ? mappedAge : null);
                } else {
                    updateStateIfChanged(setEventAgeDataApi, null);
                }

                let viewingResult;
                let packResult;

                if (activityResult.status === 'fulfilled') {
                    const rawActivity = extractTimeSeriesPayload(activityResult.value);
                    const mappedActivity = (Array.isArray(rawActivity) ? rawActivity : []).map((item) => ({
                        label: normalizeChartLabel(item?.label || item?.date || item?.period || ''),
                        viewing: clampValue(item?.viewing ?? item?.views ?? item?.viewsCount ?? 0),
                        packs: clampValue(item?.packs ?? item?.packages ?? item?.packagesCount ?? item?.packagesCreated ?? 0),
                        favorites: clampValue(item?.favorites ?? item?.likes ?? item?.liked ?? item?.favoritesAdded ?? 0),
                    }));
                    const viewingRows = mappedActivity.map((item) => ({ date: item.label, value: item.viewing }));
                    const packRows = mappedActivity.map((item) => ({ date: item.label, value: item.packs }));

                    updateStateIfChanged(setEventActivitySeriesApi, mappedActivity.length ? mappedActivity : null);
                    viewingResult = { status: 'fulfilled', value: viewingRows };
                    packResult = { status: 'fulfilled', value: packRows };
                } else {
                    updateStateIfChanged(setEventActivitySeriesApi, null);
                    viewingResult = { status: 'rejected', reason: activityResult.reason };
                    packResult = { status: 'rejected', reason: activityResult.reason };
                }

                if (viewingResult.status === 'fulfilled') {
                    const series = buildSeriesFromResponse(viewingResult.value, eventsRange, {
                        year: eventsYear,
                        month: eventsMonth,
                        startValue: eventsWeekStartDate,
                        endValue: eventsWeekEndDate,
                    });
                    updateStateIfChanged(setEventViewingSeriesApi, series.length ? series : null);
                } else {
                    console.error('Błąd pobierania statystyk wyświetleń wydarzenia:', viewingResult.reason);
                    updateStateIfChanged(setEventViewingSeriesApi, null);
                }

                if (packResult.status === 'fulfilled') {
                    const series = buildSeriesFromResponse(packResult.value, eventsRange, {
                        year: eventsYear,
                        month: eventsMonth,
                        startValue: eventsWeekStartDate,
                        endValue: eventsWeekEndDate,
                    });
                    updateStateIfChanged(setEventPackSeriesApi, series.length ? series : null);
                } else {
                    console.error('Błąd pobierania statystyk paczek dla wydarzenia:', packResult.reason);
                    updateStateIfChanged(setEventPackSeriesApi, null);
                }
            })
            .catch((error) => {
                console.error('Błąd pobierania statystyk wydarzenia:', error);
                if (!isMounted) return;
                updateStateIfChanged(setEventViewingSeriesApi, null);
                updateStateIfChanged(setEventPackSeriesApi, null);
                updateStateIfChanged(setEventActivitySeriesApi, null);
                updateStateIfChanged(setEventSummaryApi, null);
                updateStateIfChanged(setEventAgeDataApi, null);
            });

        return () => {
            isMounted = false;
        };
    }, [
        activeTab,
        selectedEventStatsId,
        eventsRange,
        eventsYear,
        eventsMonth,
        eventsWeekStartDate,
        eventsWeekEndDate,
        refreshTick,
    ]);

    useEffect(() => {
        if (activeTab !== EVENT_DETAIL_TAB && eventInfoModalKey) {
            setEventInfoModalKey(null);
        }
    }, [activeTab, eventInfoModalKey]);

    useEffect(() => {
        if (!eventInfoModalKey) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setEventInfoModalKey(null);
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => {
            window.removeEventListener('keydown', handleEscape);
        };
    }, [eventInfoModalKey]);

    const usersRegisteredDemography = useMemo(
        () => usersSeriesApi?.demography?.registered || null,
        [usersSeriesApi]
    );
    const usersRegisteredCount = useMemo(
        () => (
            findMetricValueByKeys(usersDemographicsApi, ['totalProfiles'])
            || findMetricValueByKeys(usersSummaryApi, [
                'totalRegistered',
                'registeredUsers',
                'totalRegisteredUsers',
                'totalRegisteredCount',
            ])
            || 0
        ),
        [usersDemographicsApi, usersSummaryApi]
    );
    const usersGenderShareBase = useMemo(() => {
        if (usersDemographicsApi?.genderDistribution || usersDemographicsApi?.genderTotals) {
            const rows = mapGenderRowsFromDemographics(usersDemographicsApi, usersRegisteredCount);

            return [
                { key: 'K', label: GENDER_LABELS.K, color: GENDER_COLORS.K, percent: rows.K.percent, count: rows.K.count },
                { key: 'M', label: GENDER_LABELS.M, color: GENDER_COLORS.M, percent: rows.M.percent, count: rows.M.count },
                { key: 'I', label: GENDER_LABELS.I, color: GENDER_COLORS.I, percent: rows.I.percent, count: rows.I.count },
            ];
        }

        const resolved = resolveGenderDemography(usersRegisteredDemography?.gender || {});
        return [
            { key: 'K', label: GENDER_LABELS.K, color: GENDER_COLORS.K, percent: resolved.rows.K.percent, count: resolved.rows.K.count },
            { key: 'M', label: GENDER_LABELS.M, color: GENDER_COLORS.M, percent: resolved.rows.M.percent, count: resolved.rows.M.count },
            { key: 'I', label: GENDER_LABELS.I, color: GENDER_COLORS.I, percent: resolved.rows.I.percent, count: resolved.rows.I.count },
        ];
    }, [usersDemographicsApi, usersRegisteredCount, usersRegisteredDemography]);
    const usersGenderShareData = useMemo(
        () => applyGenderFilterToShare(usersGenderShareBase, usersGender),
        [usersGenderShareBase, usersGender]
    );
    const usersGenderRatio = useMemo(
        () => getGenderRatioForFilter(usersGenderShareBase, usersGender),
        [usersGenderShareBase, usersGender]
    );
    const usersGenderTotal = useMemo(() => {
        if (usersGender === 'ALL') {
            return usersGenderShareData.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
        }

        const selected = usersGenderShareBase.find((item) => item.key === usersGender);
        return Number(selected?.count) || 0;
    }, [usersGenderShareData, usersGenderShareBase, usersGender]);

    const usersRegisteredSeriesBase = useMemo(() => usersSeriesApi?.registered || [], [usersSeriesApi]);
    const usersNewSeriesBase = useMemo(() => usersSeriesApi?.newUsers || [], [usersSeriesApi]);
    const usersActiveSeriesBase = useMemo(() => usersSeriesApi?.activeUsers || [], [usersSeriesApi]);
    const usersPackSeriesBase = useMemo(() => usersSeriesApi?.packActivity || packSeriesApi || [], [usersSeriesApi, packSeriesApi]);
    const usersConfirmedSeriesBase = useMemo(() => usersSeriesApi?.confirmedOutings || [], [usersSeriesApi]);
    const usersAccountDeletionsSeriesBase = useMemo(() => usersSeriesApi?.accountDeletions || [], [usersSeriesApi]);
    const usersReportsSeriesBase = useMemo(() => usersSeriesApi?.reports || [], [usersSeriesApi]);
    const usersRegisteredSeries = useMemo(
        () => applyGenderFilterToSeries(usersRegisteredSeriesBase, usersGenderRatio),
        [usersRegisteredSeriesBase, usersGenderRatio]
    );
    const usersNewSeries = useMemo(
        () => applyGenderFilterToSeries(usersNewSeriesBase, usersGenderRatio),
        [usersNewSeriesBase, usersGenderRatio]
    );
    const usersActiveSeries = useMemo(
        () => applyGenderFilterToSeries(usersActiveSeriesBase, usersGenderRatio),
        [usersActiveSeriesBase, usersGenderRatio]
    );
    const usersPackSeries = useMemo(
        () => applyGenderFilterToSeries(usersPackSeriesBase, usersGenderRatio),
        [usersPackSeriesBase, usersGenderRatio]
    );
    const usersConfirmedSeries = useMemo(
        () => usersConfirmedSeriesBase,
        [usersConfirmedSeriesBase]
    );
    const usersAccountDeletionsSeries = useMemo(
        () => usersAccountDeletionsSeriesBase,
        [usersAccountDeletionsSeriesBase]
    );
    const usersReportsSeries = useMemo(
        () => usersReportsSeriesBase,
        [usersReportsSeriesBase]
    );

    const organizersRegisteredSeries = useMemo(() => organizersSeriesApi?.registered || [], [organizersSeriesApi]);
    const organizersNewSeries = useMemo(() => organizersSeriesApi?.newOrganizers || [], [organizersSeriesApi]);
    const organizersZeroSeries = useMemo(
        () => buildSeriesFromResponse([], organizersRange, {
            year: organizersYear,
            month: organizersMonth,
            startValue: organizersWeekStartDate,
            endValue: organizersWeekEndDate,
        }),
        [organizersRange, organizersYear, organizersMonth, organizersWeekStartDate, organizersWeekEndDate]
    );
    const organizersPurchasedPromotionsSeries = useMemo(
        () => (
            organizersSeriesApi?.purchasedPromotions?.length
                ? organizersSeriesApi.purchasedPromotions
                : organizersZeroSeries
        ),
        [organizersSeriesApi, organizersZeroSeries]
    );
    const organizersPromotionsValueSeries = useMemo(
        () => (
            organizersSeriesApi?.promotionsValue?.length
                ? organizersSeriesApi.promotionsValue
                : organizersZeroSeries
        ),
        [organizersSeriesApi, organizersZeroSeries]
    );
    const organizersKpiCards = useMemo(() => ([
        {
            key: 'purchasedPromotions',
            label: 'Liczba zakupionych promocji',
            value: '0',
            note: 'w wybranym okresie',
        },
        {
            key: 'promotionsValue',
            label: 'Wartość promocji (zł)',
            value: '0',
            note: 'w wybranym okresie',
        },
    ]), []);
    const eventsAvgParticipantsSeries = useMemo(() => (
        eventsAvgParticipantsSeriesApi?.length
            ? eventsAvgParticipantsSeriesApi
            : []
    ), [eventsAvgParticipantsSeriesApi]);

    const eventsAvgParticipantsCard = useMemo(() => {
        return {
            key: 'avgParticipants',
            label: 'Średnia liczba uczestników',
            value: formatStatNumber(getTrendLastValue(eventsAvgParticipantsSeries)),
            note: eventsSummaryMode === 'ACTIVE' ? 'dla aktywnych wydarzeń' : 'w wybranym okresie',
        };
    }, [eventsAvgParticipantsSeries, eventsSummaryMode]);

    const eventsAvgParticipantsCardResolved = useMemo(() => {
        const averageParticipantsPayload = eventsSummaryApi?.averageParticipants;
        const averageParticipantsValue = toSafeMetricNumber(
            averageParticipantsPayload?.value ?? eventsSummaryApi?.averageParticipants
        );
        const averageParticipantsTotal = toSafeMetricNumber(averageParticipantsPayload?.totalParticipants);
        const averageParticipantsEventsCount = toSafeMetricNumber(averageParticipantsPayload?.eventsCount);
        const hasAverageDetails = averageParticipantsTotal != null && averageParticipantsEventsCount != null;

        return {
            ...eventsAvgParticipantsCard,
            value: formatStatNumber(
                averageParticipantsValue != null
                    ? averageParticipantsValue
                    : getTrendLastValue(eventsAvgParticipantsSeries)
            ),
            note: hasAverageDetails
                ? `${formatStatNumber(averageParticipantsTotal)} uczestników / ${formatStatNumber(averageParticipantsEventsCount)} wydarzeń`
                : eventsAvgParticipantsCard.note,
        };
    }, [eventsAvgParticipantsCard, eventsAvgParticipantsSeries, eventsSummaryApi?.averageParticipants]);

    const eventsCategoryFilters = useMemo(
        () => rankingCategories.length
            ? rankingCategories
            : [{ value: '0', label: 'Wszystkie', apiValue: '0' }],
        [rankingCategories]
    );
    const selectedEventsCategory = String(eventsCategory ?? '0');
    const eventsInterestSource = useMemo(() => (
        eventsTopInterestApi?.length ? eventsTopInterestApi : []
    ), [eventsTopInterestApi]);
    const eventsPacksSource = useMemo(() => (
        eventsTopPacksApi?.length ? eventsTopPacksApi : []
    ), [eventsTopPacksApi]);
    const eventsInterestItems = useMemo(
        () => (
            [...eventsInterestSource]
                .map((item) => ({
                    ...item,
                    metricValue: eventsMetric === 'views'
                        ? toSafeMetricNumber(item?.views)
                        : toSafeMetricNumber(item?.likes),
                }))
                .filter((item) => item.metricValue != null)
                .sort((left, right) => right.metricValue - left.metricValue)
        ),
        [eventsInterestSource, eventsMetric]
    );
    const eventsTopPacksItems = useMemo(
        () => (
            [...eventsPacksSource]
                .map((item) => ({
                    ...item,
                    metricValue: toSafeMetricNumber(item?.metricValue) ?? toSafeMetricNumber(item?.packs) ?? toSafeMetricNumber(item?.value),
                }))
                .filter((item) => item.metricValue != null)
                .sort((left, right) => right.metricValue - left.metricValue)
        ),
        [eventsPacksSource]
    );
    const eventsCategoryBreakdownItems = useMemo(() => {
        const apiCategories = extractCollectionPayload(
            eventsCategoriesApi?.categories
            || eventsCategoriesApi
        );
        if (Array.isArray(apiCategories) && apiCategories.length && apiCategories.some((item) => item?.category || item?.count != null)) {
            return apiCategories.map((item) => ({
                category: normalizeChartLabel(item?.category || item?.name || item?.label || 'Kategoria'),
                percent: clampValue(item?.percentage ?? item?.percent ?? item?.share ?? 0),
                count: clampValue(item?.count ?? item?.value ?? 0),
            }));
        }

        return [];
    }, [eventsCategoriesApi]);

    const eventsTotalEventsCard = useMemo(() => {
        const apiTotal = findMetricValueByKeys(eventsCategoriesApi, ['totalEvents', 'eventsCount', 'total'])
            ?? findMetricValueByKeys(eventsSummaryApi, ['totalEvents', 'eventsCount', 'total']);
        const totalCount = apiTotal != null
            ? apiTotal
            : eventsCategoryBreakdownItems.reduce((sum, item) => sum + (Number(item.count) || 0), 0);

        return {
            key: 'all',
            label: 'Ogółem',
            value: formatStatNumber(totalCount),
            note: eventsSummaryMode === 'ACTIVE' ? 'aktywne wydarzenia' : 'wszystkie wydarzenia',
        };
    }, [eventsCategoriesApi, eventsSummaryApi, eventsCategoryBreakdownItems, eventsSummaryMode]);

    const ageData = useMemo(() => {
        const fromDemographicsEndpoint = mapAgeRowsFromDemographicsApi(
            usersDemographicsApi,
            usersGender
        );
        if (fromDemographicsEndpoint.length) {
            return fromDemographicsEndpoint;
        }

        const ages = usersRegisteredDemography?.ages || [];
        if (!ages.length) return [];

        return ages.map((item) => {
            const percent = clampValue(item?.percent ?? 0);
            const explicitCount = toSafeMetricNumber(item?.count);
            const estimatedCount = usersGenderTotal > 0 ? clampValue((usersGenderTotal * percent) / 100) : 0;
            const count = explicitCount != null ? clampValue(explicitCount) : estimatedCount;
            return {
                range: String(item?.range || ''),
                percent,
                count,
            };
        });
    }, [usersDemographicsApi, usersGender, usersRegisteredDemography, usersGenderTotal]);
    const cityData = useMemo(
        () => ([
            {
                city: 'Warszawa',
                percent: 100,
                count: clampValue(usersRegisteredCount || usersGenderTotal || 0),
            },
        ]),
        [usersRegisteredCount, usersGenderTotal]
    );
    const usersSummaryByKey = useMemo(
        () => {
            const defaults = [...USERS_SUMMARY, ...USERS_PAIRED_EXTRA_SUMMARY]
                .reduce((acc, item) => ({ ...acc, [item.key]: item }), {});

            const registeredCount = findMetricValueByKeys(usersSummaryApi, [
                'totalRegistered',
                'registeredUsers',
                'totalRegisteredUsers',
                'totalRegistered',
                'totalRegisteredCount',
                'totalProfiles',
                'registered',
            ]) ?? (usersRegisteredCount > 0 ? usersRegisteredCount : null);

            const newSeriesSum = usersNewSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
            const newCount = findMetricValueByKeys(usersSummaryApi, ['newUsers', 'newUsersInPeriod', 'newUsersCount', 'new'])
                ?? (usersNewSeries.length ? newSeriesSum : null);

            const activeCount = findMetricValueByKeys(usersSummaryApi, ['activeUsers', 'activeUsersInPeriod', 'activeUsersCount', 'active']);
            const inactiveCount = findMetricValueByKeys(usersSummaryApi, ['unloggedUsers', 'inactiveUsersInPeriod', 'inactiveUsers', 'inactiveUsersCount', 'inactive']);

            const packSeriesSum = usersPackSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
            const packagesCount = findMetricValueByKeys(usersSummaryApi, ['packageActivity', 'packagesActivityCount', 'packagesCount', 'packagesActivity', 'packActivity'])
                ?? (usersPackSeries.length ? packSeriesSum : null);

            const eventParticipationPayload = usersSummaryApi?.eventParticipation || {};
            const confirmedCount = findMetricValueByKeys(
                eventParticipationPayload,
                ['confirmed', 'confirmedCount', 'eventParticipationConfirmedCount']
            );
            const participationTotal = findMetricValueByKeys(
                eventParticipationPayload,
                ['totalDeclared', 'declared', 'eventParticipationDeclaredCount', 'participationDeclaredCount']
            );

            const deletedSeriesSum = usersAccountDeletionsSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
            const deletedCount = findMetricValueByKeys(usersSummaryApi, [
                'deletedAccountsCount',
                'deletedUsers',
                'deletedAccounts',
                'accountDeletions',
                'accountDeletionsCount',
                'deletedCount',
                'deleted',
                'deletions',
                'totalDeletedAccounts',
                'totalDeleted',
            ]) ?? (usersAccountDeletionsSeries.length ? deletedSeriesSum : null);

            const reportsSeriesSum = usersReportsSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
            const reportsCount = findMetricValueByKeys(usersSummaryApi, [
                'reportsCount',
                'reports',
                'userReports',
                'totalReports',
                'reportsTotal',
                'messageReportsCount',
                'profileReportsCount',
            ]) ?? (usersReportsSeries.length ? reportsSeriesSum : null);

            const resolvedInactive = inactiveCount != null && inactiveCount >= 0
                ? inactiveCount
                : ((registeredCount != null && activeCount != null) ? Math.max(0, registeredCount - activeCount) : null);

            const confirmedRateFromPayload = findMetricValueByKeys(
                eventParticipationPayload,
                ['percentage', 'eventParticipationConfirmedRate']
            );
            const confirmedRate = confirmedRateFromPayload != null
                ? confirmedRateFromPayload
                : (participationTotal > 0
                    ? ((confirmedCount ?? 0) / participationTotal) * 100
                    : (findMetricValueByKeys(usersSummaryApi, ['eventParticipationConfirmedRate']) ?? 0));
            const formatParticipationRate = (value) => {
                if (value == null || !Number.isFinite(Number(value))) return '0%';
                const rounded = Math.round(Number(value) * 10) / 10;
                return `${rounded.toLocaleString('pl-PL', {
                    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 1,
                    maximumFractionDigits: 1,
                })}%`;
            };

            return {
                ...defaults,
                ...(registeredCount != null && { registered: { ...defaults.registered, value: formatStatNumber(registeredCount) } }),
                ...(newCount != null && { new: { ...defaults.new, value: formatStatNumber(newCount) } }),
                ...(activeCount != null && { active: { ...defaults.active, value: formatStatNumber(activeCount) } }),
                ...(resolvedInactive != null && { inactive: { ...defaults.inactive, value: formatStatNumber(resolvedInactive) } }),
                ...(packagesCount != null && { pack: { ...defaults.pack, value: formatStatNumber(packagesCount) } }),
                ...(confirmedRate != null && {
                    confirmed: {
                        ...defaults.confirmed,
                        value: formatParticipationRate(confirmedRate),
                        note: participationTotal > 0 ? `${formatStatNumber(confirmedCount || 0)} / ${formatStatNumber(participationTotal)} wyjść` : defaults.confirmed.note,
                    },
                }),
                ...(deletedCount != null && { accountDeletion: { ...defaults.accountDeletion, value: formatStatNumber(deletedCount) } }),
                ...(reportsCount != null && { reports: { ...defaults.reports, value: formatStatNumber(reportsCount) } }),
            };
        },
        [
            usersSummaryApi,
            usersRegisteredCount,
            usersNewSeries,
            usersPackSeries,
            usersAccountDeletionsSeries,
            usersReportsSeries,
        ]
    );
    const organizersSummaryByKey = useMemo(
        () => {
            const defaults = ORGANIZERS_SUMMARY.reduce((acc, item) => ({ ...acc, [item.key]: item }), {});
            if (!organizersSummaryApi) return defaults;

            const totalOrganizers = findMetricValueByKeys(organizersSummaryApi, ['totalOrganizers', 'registeredOrganizers']);
            const newOrganizers = findMetricValueByKeys(organizersSummaryApi, ['newOrganizersInPeriod', 'newOrganizers']);

            return {
                ...defaults,
                ...(totalOrganizers != null && { registered: { ...defaults.registered, value: formatStatNumber(totalOrganizers) } }),
                ...(newOrganizers != null && { new: { ...defaults.new, value: formatStatNumber(newOrganizers) } }),
            };
        },
        [organizersSummaryApi]
    );
    const organizersKpiByKey = useMemo(
        () => organizersKpiCards.reduce((acc, item) => ({ ...acc, [item.key]: item }), {}),
        [organizersKpiCards]
    );
    const selectedAgeBreakdown = useMemo(
        () => usersRegisteredDemography?.ages || [],
        [usersRegisteredDemography]
    );
    const getMetricDemography = useCallback((metricKey) => {
        const metricDemography = usersSeriesApi?.demography?.[metricKey] || null;
        const normalizedGender = normalizeGenderBreakdown(normalizeDemographicGender(metricDemography?.gender || {}));
        const agesBase = metricDemography?.ages || selectedAgeBreakdown || [];
        let filteredGender = normalizedGender;

        if (usersGender !== 'ALL') {
            const selectedEntry = normalizeGenderValue(normalizedGender[usersGender]);
            filteredGender = {
                K: { percent: usersGender === 'K' ? 100 : 0, count: usersGender === 'K' ? selectedEntry.count : 0 },
                M: { percent: usersGender === 'M' ? 100 : 0, count: usersGender === 'M' ? selectedEntry.count : 0 },
                I: { percent: usersGender === 'I' ? 100 : 0, count: usersGender === 'I' ? selectedEntry.count : 0 },
            };
        }

        return {
            gender: scaleGenderBreakdownCounts(filteredGender, 1),
            ages: agesBase,
        };
    }, [usersGender, selectedAgeBreakdown, usersSeriesApi]);
    const usersNewDemography = useMemo(() => getMetricDemography('newUsers'), [getMetricDemography]);
    const usersActiveDemography = useMemo(() => getMetricDemography('activeUsers'), [getMetricDemography]);
    const usersPackDemography = useMemo(() => getMetricDemography('packActivity'), [getMetricDemography]);
    const rankingLikesItems = useMemo(
        () => (
            [...(rankingInterest || [])]
                .sort((left, right) => (toSafeMetricNumber(right?.likes) || 0) - (toSafeMetricNumber(left?.likes) || 0))
                .slice(0, RANKING_LIST_LIMIT)
                .map((item, index) => {
                    const eventIdKey = normalizeEventIdKey(item?.id ?? item?.eventId);
                    const meta = eventIdKey ? rankingEventMetaById[eventIdKey] : null;
                    const name = hasUsableEventName(item?.name)
                        ? String(item.name).trim()
                        : (hasUsableEventName(meta?.name)
                            ? String(meta.name).trim()
                            : `Wydarzenie #${eventIdKey || index + 1}`);
                    const place = String(item?.place || meta?.place || '').trim();
                    const city = String(item?.city || meta?.city || '').trim();
                    const locationLabel = [place, city].filter(Boolean).join(', ') || 'Brak lokalizacji';
                    const startDateRaw = pickFirstDefined(item?.startDateRaw, meta?.startDateRaw, item?.date);
                    const date = formatRankingDate(startDateRaw || item?.date);
                    const time = formatRankingTime(startDateRaw || item?.date);

                    return {
                        ...item,
                        rank: index + 1,
                        name,
                        place,
                        city,
                        locationLabel,
                        date,
                        time,
                        dateTimeLabel: time && time !== '-' ? `${date}, ${time}` : date,
                    };
                })
        ),
        [rankingInterest, rankingEventMetaById]
    );
    const rankingJoinItems = useMemo(
        () => (
            [...(rankingPacks || [])]
                .sort((left, right) => (toSafeMetricNumber(right?.packs) || 0) - (toSafeMetricNumber(left?.packs) || 0))
                .slice(0, RANKING_LIST_LIMIT)
                .map((item, index) => {
                    const eventIdKey = normalizeEventIdKey(item?.id ?? item?.eventId);
                    const meta = eventIdKey ? rankingEventMetaById[eventIdKey] : null;
                    const name = hasUsableEventName(item?.name)
                        ? String(item.name).trim()
                        : (hasUsableEventName(meta?.name)
                            ? String(meta.name).trim()
                            : `Wydarzenie #${eventIdKey || index + 1}`);
                    const place = String(item?.place || meta?.place || '').trim();
                    const city = String(item?.city || meta?.city || '').trim();
                    const locationLabel = [place, city].filter(Boolean).join(', ') || 'Brak lokalizacji';
                    const startDateRaw = pickFirstDefined(item?.startDateRaw, meta?.startDateRaw, item?.date);
                    const date = formatRankingDate(startDateRaw || item?.date);
                    const time = formatRankingTime(startDateRaw || item?.date);

                    return {
                        ...item,
                        rank: index + 1,
                        name,
                        place,
                        city,
                        locationLabel,
                        date,
                        time,
                        dateTimeLabel: time && time !== '-' ? `${date}, ${time}` : date,
                    };
                })
        ),
        [rankingPacks, rankingEventMetaById]
    );
    const rankingListConfig = useMemo(
        () => (
            rankingVisibleList === 'packs'
                ? {
                    keyPrefix: 'packs',
                    titleAccent: 'Top 10',
                    titleSuffix: 'wydarzeń według dołączeń',
                    infoTitle: 'Wydarzenia według dołączeń',
                    infoDescription: 'Utworzenie lub dołączenie do paczki pod wydarzeniem.',
                    items: rankingJoinItems,
                }
                : {
                    keyPrefix: 'interest',
                    titleAccent: 'Top 10',
                    titleSuffix: 'wydarzeń według polubień',
                    infoTitle: 'Wydarzenia według polubień',
                    infoDescription: 'Liczba użytkowników, którzy polubili wydarzenie.',
                    items: rankingLikesItems,
                }
        ),
        [rankingVisibleList, rankingLikesItems, rankingJoinItems]
    );
    const resolveEventImageHash = useCallback((item) => (
        extractEventImageHash(item, true) || extractEventImageHash(item?.statsPayload, true)
    ), []);
    const fetchEventImageByHash = useCallback(async (imageHashCode) => {
        if (!imageHashCode || !accessToken) return null;

        try {
            const imgResponse = await fetch(`/api/events-images/${imageHashCode}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                credentials: 'include',
                mode: 'cors',
            });

            if (!imgResponse.ok) return null;

            const blob = await imgResponse.blob();
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error(`Błąd pobierania obrazka wydarzenia ${imageHashCode}:`, error);
            return null;
        }
    }, [accessToken]);
    const resolveEventImageHashFromApi = useCallback(async (eventId, initialHash = null) => {
        const normalizedInitial = pickRawImageId(initialHash);
        if (normalizedInitial && hasEventImagePrefix(normalizedInitial)) return normalizedInitial;
        const eventIdKey = normalizeEventIdKey(eventId);
        if (!eventIdKey) return normalizedInitial || null;

        if (!isUuidLike(eventIdKey)) {
            try {
                const eventResponse = await ApiService.getEvent(eventIdKey);
                const fromEvent = extractEventImageHash(eventResponse?.data);
                if (fromEvent) return fromEvent;
            } catch (error) {
                console.error(`Blad pobierania eventu ${eventId} dla obrazka:`, error);
            }
        }

        try {
            const imagesResponse = await ApiService.getAllImagesForEvent(eventIdKey);
            const imageIds = Array.isArray(imagesResponse?.data) ? imagesResponse.data : [];
            const firstEventPrefixed = imageIds.find((imageId) => hasEventImagePrefix(imageId));
            if (firstEventPrefixed) return String(firstEventPrefixed);
            const firstImage = pickRawImageId(imageIds[0]);
            if (firstImage) return firstImage;
        } catch (error) {
            console.error(`Błąd pobierania listy obrazków eventu ${eventId}:`, error);
        }

        return normalizedInitial || null;
    }, []);
    const imageCandidateItems = useMemo(() => {
        const byEventId = new Map();
        const pushCandidate = (item, allowMissingHashLookup = false) => {
            const eventIdKey = normalizeEventIdKey(item?.id ?? item?.eventId);
            if (!eventIdKey) return;

            const imageHashCode = resolveEventImageHash(item);
            if (!imageHashCode && !allowMissingHashLookup) return;

            const existing = byEventId.get(eventIdKey);
            if (existing) {
                const existingHasPrefix = hasEventImagePrefix(existing.imageHashCode);
                const nextHasPrefix = hasEventImagePrefix(imageHashCode);
                if (existingHasPrefix || !nextHasPrefix) return;
            }

            byEventId.set(eventIdKey, {
                eventId: eventIdKey,
                imageHashCode,
            });
        };

        (eventStatsList || []).forEach((item) => pushCandidate(item, false));
        (rankingLikesItems || []).forEach((item) => pushCandidate(item, true));
        (rankingJoinItems || []).forEach((item) => pushCandidate(item, true));

        return Array.from(byEventId.values());
    }, [eventStatsList, rankingLikesItems, rankingJoinItems, resolveEventImageHash]);
    useEffect(() => {
        if (activeTab !== 'Wydarzenia' && activeTab !== EVENT_DETAIL_TAB && activeTab !== 'Rankingi') return;
        if (!imageCandidateItems.length) return;

        imageCandidateItems.forEach(({ eventId, imageHashCode }) => {
            const candidateHash = imageHashCode || '__missing__';
            const previousHash = fetchedImageHashesByEventRef.current.get(eventId);
            const alreadyLoaded = Boolean(eventImageUrlById[eventId]);
            if (previousHash === candidateHash && !alreadyLoaded) return;
            if (previousHash === imageHashCode && alreadyLoaded) return;
            if (imageFetchInFlightByEventRef.current.has(eventId)) return;

            imageFetchInFlightByEventRef.current.add(eventId);

            resolveEventImageHashFromApi(eventId, imageHashCode)
                .then((resolvedHash) => {
                    if (!resolvedHash) {
                        fetchedImageHashesByEventRef.current.set(eventId, candidateHash);
                        return null;
                    }
                    return fetchEventImageByHash(resolvedHash)
                        .then((imageObjectUrl) => ({ imageObjectUrl, resolvedHash }));
                })
                .then((imageObjectUrl) => {
                    if (!imageObjectUrl?.imageObjectUrl) {
                        const failedHash = imageObjectUrl?.resolvedHash || candidateHash;
                        fetchedImageHashesByEventRef.current.set(eventId, failedHash);
                        return;
                    }

                    const previousObjectUrl = objectUrlByEventRef.current.get(eventId);
                    if (previousObjectUrl && previousObjectUrl !== imageObjectUrl.imageObjectUrl) {
                        URL.revokeObjectURL(previousObjectUrl);
                    }

                    fetchedImageHashesByEventRef.current.set(eventId, imageObjectUrl.resolvedHash);
                    objectUrlByEventRef.current.set(eventId, imageObjectUrl.imageObjectUrl);
                    setEventImageUrlById((previous) => ({
                        ...previous,
                        [eventId]: imageObjectUrl.imageObjectUrl,
                    }));
                })
                .finally(() => {
                    imageFetchInFlightByEventRef.current.delete(eventId);
                });
        });
    }, [activeTab, eventImageUrlById, imageCandidateItems, fetchEventImageByHash, resolveEventImageHashFromApi]);
    useEffect(() => () => {
        objectUrlByEventRef.current.forEach((url) => {
            if (url) URL.revokeObjectURL(url);
        });
        objectUrlByEventRef.current.clear();
        fetchedImageHashesByEventRef.current.clear();
        imageFetchInFlightByEventRef.current.clear();
        eventStatsPageFetchInFlightRef.current.clear();
    }, []);
    const eventStatsFilteredList = useMemo(() => {
        return eventStatsList;
    }, [eventStatsList]);

    const handleEventStatsListScroll = useCallback(() => {
        // Endpoint administratora zwraca kompletną, posortowaną listę.
        // Scroll pozostaje obsługiwany, aby nie zmieniać layoutu panelu.
    }, []);
    useEffect(() => {
        if (activeTab !== EVENT_DETAIL_TAB) return;
        if (!eventStatsFilteredList.length) return;

        const candidates = eventStatsFilteredList
            .slice(0, 15)
            .filter((item) => {
                const eventId = normalizeEventIdKey(item?.id);
                if (!eventId) return false;
                if (isUuidLike(eventId)) return false;
                if (eventDetailFetchInFlightRef.current.has(eventId)) return false;
                if (isInRetryCooldown(eventDetailFetchFailedRef, eventId)) return false;

                const hasName = hasUsableEventName(item?.name);
                const hasDate = Boolean(parseStatsEventDateValue(item?.startDate || item?.date || ''));
                const hasPlaceOrCity = Boolean(String(item?.place || '').trim() || String(item?.city || '').trim());
                const hasOrg = hasOrganizerName(item?.organizerName);
                return !(hasName && hasDate && (hasPlaceOrCity || hasOrg));
            })
            .map((item) => normalizeEventIdKey(item?.id))
            .filter(Boolean);

        if (!candidates.length) return;

        candidates.forEach((eventId) => {
            eventDetailFetchInFlightRef.current.add(eventId);
            ApiService.getEvent(eventId)
                .then((response) => {
                    const mapped = ensureOrganizerNameForEventItem(mapStatsEventToSidebarItem(response?.data || {}));
                    if (!mapped) {
                        eventDetailFetchFailedRef.current.set(eventId, Date.now());
                        return;
                    }
                    eventDetailFetchFailedRef.current.delete(eventId);
                    if (mapped.ownerId && hasOrganizerName(mapped.organizerName)) {
                        organizerNameByOwnerIdRef.current.set(mapped.ownerId, mapped.organizerName);
                    }
                    setEventStatsList((previous) => upsertStatsEvent(previous, mapped));
                })
                .catch(() => {
                    eventDetailFetchFailedRef.current.set(eventId, Date.now());
                })
                .finally(() => {
                    eventDetailFetchInFlightRef.current.delete(eventId);
                });
        });
    }, [activeTab, ensureOrganizerNameForEventItem, eventStatsFilteredList, isInRetryCooldown]);
    const resolveRenderedEventImageUrl = useCallback((item) => {
        const eventIdKey = normalizeEventIdKey(item?.id ?? item?.eventId);
        if (eventIdKey) {
            const resolvedObjectUrl = eventImageUrlById[eventIdKey];
            if (resolvedObjectUrl) return resolvedObjectUrl;
        }
        const directImageUrl = pickRawImageId(
            item?.imageUrl,
            item?.image_url,
            item?.imageHashCode,
            item?.image_hash_code,
            item?.image,
            item?.statsPayload?.imageUrl,
            item?.statsPayload?.image_url,
            item?.statsPayload?.imageHashCode,
            item?.statsPayload?.image_hash_code,
            item?.statsPayload?.image
        );
        if (isLikelyDirectImageUrl(directImageUrl)) return directImageUrl;
        return '';
    }, [eventImageUrlById]);
    const selectedEventStatsData = useMemo(
        () => eventStatsList.find((item) => normalizeEventIdKey(item.id) === normalizeEventIdKey(selectedEventStatsId)) || null,
        [selectedEventStatsId, eventStatsList]
    );
    const selectedEventLocation = useMemo(
        () => [selectedEventStatsData?.place, selectedEventStatsData?.city].filter(Boolean).join(', '),
        [selectedEventStatsData]
    );
    const selectedEventDate = useMemo(
        () => formatSidebarEventDate(selectedEventStatsData),
        [selectedEventStatsData]
    );
    const eventViewingSeries = useMemo(() => {
        return eventViewingSeriesApi?.length ? eventViewingSeriesApi : [];
    }, [eventViewingSeriesApi]);
    const eventPackSeries = useMemo(() => {
        return eventPackSeriesApi?.length ? eventPackSeriesApi : [];
    }, [eventPackSeriesApi]);
    const eventFavoritesSeries = useMemo(() => {
        if (eventActivitySeriesApi?.length) {
            return eventActivitySeriesApi.map((item) => ({
                label: item.label,
                value: clampValue(item.favorites || 0),
            }));
        }

        return [];
    }, [eventActivitySeriesApi]);
    const eventActivitySeries = useMemo(() => {
        if (eventActivitySeriesApi?.length) {
            return eventActivitySeriesApi;
        }

        const allLabels = Array.from(new Set([
            ...(eventViewingSeries || []).map((item) => item.label),
            ...(eventPackSeries || []).map((item) => item.label),
            ...(eventFavoritesSeries || []).map((item) => item.label),
        ]));
        const viewsByLabel = new Map((eventViewingSeries || []).map((item) => [item.label, Number(item.value) || 0]));
        const packsByLabel = new Map((eventPackSeries || []).map((item) => [item.label, Number(item.value) || 0]));
        const favoritesByLabel = new Map((eventFavoritesSeries || []).map((item) => [item.label, Number(item.value) || 0]));

        return allLabels.map((label) => ({
            label,
            viewing: viewsByLabel.get(label) || 0,
            packs: packsByLabel.get(label) || 0,
            favorites: favoritesByLabel.get(label) || 0,
        }));
    }, [eventActivitySeriesApi, eventViewingSeries, eventPackSeries, eventFavoritesSeries]);
    const eventSummaryViewsGender = useMemo(
        () => eventSummaryApi?.views?.genderBreakdown || null,
        [eventSummaryApi]
    );
    const eventSummaryPacksGender = useMemo(
        () => eventSummaryApi?.packageParticipants?.genderBreakdown || null,
        [eventSummaryApi]
    );
    const eventViewsTotal = useMemo(() => {
        const apiValue = findMetricValueByKeys(eventSummaryApi?.views, ['total']);
        if (apiValue != null) return clampValue(apiValue);
        return eventViewingSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
    }, [eventSummaryApi, eventViewingSeries]);
    const eventPacksTotal = useMemo(() => {
        const apiValue = findMetricValueByKeys(eventSummaryApi?.packageParticipants, ['total']);
        if (apiValue != null) return clampValue(apiValue);
        return eventPackSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
    }, [eventSummaryApi, eventPackSeries]);
    const eventFavoritesTotal = useMemo(
        () => eventFavoritesSeries.reduce((sum, item) => sum + (Number(item?.value) || 0), 0),
        [eventFavoritesSeries]
    );
    const eventConversionRate = useMemo(() => {
        const apiValue = findMetricValueByKeys(eventSummaryApi?.conversionInsight, ['conversionRatePercentage']);
        if (apiValue != null) return clampValue(apiValue);
        return eventViewsTotal > 0 ? clampValue((eventPacksTotal / eventViewsTotal) * 100) : 0;
    }, [eventSummaryApi, eventViewsTotal, eventPacksTotal]);
    const eventInterestBoost = useMemo(() => {
        const apiValue = findMetricValueByKeys(eventSummaryApi?.conversionInsight, ['categoryAverageComparisonPercentage']);
        if (apiValue != null) return clampValue(apiValue);
        return eventViewsTotal > 0 ? clampValue(((eventFavoritesTotal + eventPacksTotal) / eventViewsTotal) * 100) : 0;
    }, [eventSummaryApi, eventViewsTotal, eventFavoritesTotal, eventPacksTotal]);
    const eventNewUsersCount = useMemo(
        () => {
            const apiValue = findMetricValueByKeys(eventSummaryApi?.visitorTypes?.breakdown, ['NEW', 'new']);
            if (apiValue != null) return clampValue(apiValue);
            const fallbackValue = findMetricValueByKeys(eventSummaryApi, ['newUsersCount', 'newUsers', 'newVisitorsCount']);
            if (fallbackValue != null) return clampValue(fallbackValue);
            return 0;
        },
        [eventSummaryApi]
    );
    const eventReturningUsersCount = useMemo(
        () => {
            const apiValue = findMetricValueByKeys(eventSummaryApi?.visitorTypes?.breakdown, ['RETURNING', 'returning']);
            if (apiValue != null) return clampValue(apiValue);
            const fallbackValue = findMetricValueByKeys(eventSummaryApi, ['returningUsersCount', 'returningUsers']);
            if (fallbackValue != null) return clampValue(fallbackValue);
            return Math.max(0, eventViewsTotal - eventNewUsersCount);
        },
        [eventSummaryApi, eventViewsTotal, eventNewUsersCount]
    );
    const eventViewingLegend = useMemo(() => {
        if (eventSummaryViewsGender) {
            return addPercentToLegendItems([
                { key: 'K', label: 'Kobiety', count: clampValue(eventSummaryViewsGender?.K ?? 0), color: EVENT_GENDER_CARD_COLORS.K },
                { key: 'M', label: 'Mężczyźni', count: clampValue(eventSummaryViewsGender?.M ?? 0), color: EVENT_GENDER_CARD_COLORS.M },
                { key: 'I', label: 'Inna', count: clampValue(eventSummaryViewsGender?.I ?? 0), color: EVENT_GENDER_CARD_COLORS.I },
            ]);
        }
        const [womenCount, menCount, otherCount] = buildCountsFromRatios(eventViewsTotal, [0.34, 0.46, 0.20]);
        return addPercentToLegendItems([
            { key: 'K', label: 'Kobiety', count: womenCount, color: EVENT_GENDER_CARD_COLORS.K },
            { key: 'M', label: 'Mężczyźni', count: menCount, color: EVENT_GENDER_CARD_COLORS.M },
            { key: 'I', label: 'Inna', count: otherCount, color: EVENT_GENDER_CARD_COLORS.I },
        ]);
    }, [eventSummaryViewsGender, eventViewsTotal]);
    const eventPacksLegend = useMemo(() => {
        if (eventSummaryPacksGender) {
            return addPercentToLegendItems([
                { key: 'K', label: 'Kobiety', count: clampValue(eventSummaryPacksGender?.K ?? 0), color: EVENT_GENDER_CARD_COLORS.K },
                { key: 'M', label: 'Mężczyźni', count: clampValue(eventSummaryPacksGender?.M ?? 0), color: EVENT_GENDER_CARD_COLORS.M },
                { key: 'I', label: 'Inna', count: clampValue(eventSummaryPacksGender?.I ?? 0), color: EVENT_GENDER_CARD_COLORS.I },
            ]);
        }
        const [womenCount, menCount, otherCount] = buildCountsFromRatios(eventPacksTotal, [0.22, 0.53, 0.25]);
        return addPercentToLegendItems([
            { key: 'K', label: 'Kobiety', count: womenCount, color: EVENT_GENDER_CARD_COLORS.K },
            { key: 'M', label: 'Mężczyźni', count: menCount, color: EVENT_GENDER_CARD_COLORS.M },
            { key: 'I', label: 'Inna', count: otherCount, color: EVENT_GENDER_CARD_COLORS.I },
        ]);
    }, [eventSummaryPacksGender, eventPacksTotal]);
    const eventReturningLegend = useMemo(() => (
        addPercentToLegendItems([
            { key: 'N', label: 'Nowi', count: eventNewUsersCount, color: EVENT_GENDER_CARD_COLORS.K },
            { key: 'R', label: 'Powracający', count: eventReturningUsersCount, color: EVENT_GENDER_CARD_COLORS.M },
        ])
    ), [eventNewUsersCount, eventReturningUsersCount]);
    const eventDetailScopeValue = eventsRange === 'ALL' ? 'ALL' : 'WEEK';
    const eventAgeData = useMemo(() => {
        if (eventAgeDataApi?.length) {
            const resolved = eventAgeDataApi.map((item) => {
                const totalCount = clampValue(item.count || 0);
                const selectedCount = eventAgeFilter === 'ALL'
                    ? totalCount
                    : clampValue(item?.byGender?.[eventAgeFilter] || 0);
                return {
                    ...item,
                    range: String(item.range || '').replace('–', '-'),
                    count: selectedCount,
                    percent: 0,
                };
            });

            const totalSelected = resolved.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
            return resolved.map((item) => ({
                ...item,
                percent: totalSelected > 0 ? clampValue((item.count / totalSelected) * 100) : 0,
            }));
        }

        return [];
    }, [eventAgeDataApi, eventAgeFilter]);
    const eventAgeAxisMax = useMemo(() => {
        const maxValue = Math.max(...eventAgeData.map((item) => Number(item.count) || 0), 0);
        if (!maxValue) return 80;
        return Math.max(80, Math.ceil((maxValue + 5) / 20) * 20);
    }, [eventAgeData]);

    return (
        <div>
            <Menu />
            <Content>
                <div className="stats-container">
                    <div className="stats-header">
                        <h1>Statystyki</h1>
                    </div>

                    <div className="stats-content">
                        <div className="stats-sidebar">
                            <div
                                className={`sidebar-item ${activeTab === 'Użytkownicy' ? 'active' : ''}`}
                                onClick={() => handleSidebarClick('Użytkownicy')}
                            >
                                Użytkownicy<span className="right-oriented">&#10217;</span>
                            </div>
                            <div
                                className={`sidebar-item ${activeTab === 'Organizatorzy' ? 'active' : ''}`}
                                onClick={() => handleSidebarClick('Organizatorzy')}
                            >
                                Organizatorzy<span className="right-oriented">&#10217;</span>
                            </div>
                            <div
                                className={`sidebar-item ${activeTab === 'Wydarzenia' ? 'active' : ''}`}
                                onClick={() => handleSidebarClick('Wydarzenia')}
                            >
                                Wydarzenia<span className="right-oriented">&#10217;</span>
                            </div>
                            <div
                                className={`sidebar-item ${activeTab === 'Rankingi' ? 'active' : ''}`}
                                onClick={() => handleSidebarClick('Rankingi')}
                            >
                                Rankingi wydarzeń<span className="right-oriented">&#10217;</span>
                            </div>
                            <div
                                className={`sidebar-item ${activeTab === EVENT_DETAIL_TAB ? 'active' : ''}`}
                                onClick={() => handleSidebarClick(EVENT_DETAIL_TAB)}
                            >
                                Statystyki wydarzenia<span className="right-oriented">&#10217;</span>
                            </div>

                            {activeTab === EVENT_DETAIL_TAB && (
                                <div className="stats-sidebar-events">
                                    <div className="stats-sidebar-events-title">Wydarzenia</div>
                                    <input
                                        type="text"
                                        value={eventStatsSearch}
                                        onChange={(event) => setEventStatsSearch(event.target.value)}
                                        className="stats-sidebar-events-search"
                                        placeholder="Szukaj wydarzenia"
                                    />
                                    <div className="stats-sidebar-events-list" onScroll={handleEventStatsListScroll}>
                                        {eventStatsListLoading && (
                                            <div className="stats-sidebar-events-empty">Ładowanie listy wydarzeń...</div>
                                        )}

                                        {!eventStatsListLoading && eventStatsListError && (
                                            <div className="stats-sidebar-events-empty">{eventStatsListError}</div>
                                        )}

                                        {!eventStatsListLoading && !eventStatsListError && eventStatsFilteredList.length === 0 && (
                                            <div className="stats-sidebar-events-empty">Brak wyników.</div>
                                        )}

                                        {!eventStatsListLoading && !eventStatsListError && eventStatsFilteredList.map((eventItem) => {
                                            const resolvedOrganizerLabel = hasOrganizerName(eventItem?.organizerName)
                                                ? eventItem.organizerName
                                                : ORGANIZER_NAME_FALLBACK;

                                            return (
                                                <button
                                                    key={`stats-sidebar-event-${eventItem.id}`}
                                                    type="button"
                                                    className={`stats-sidebar-event-item ${normalizeEventIdKey(selectedEventStatsId) === normalizeEventIdKey(eventItem.id) ? 'is-active' : ''}`}
                                                    onClick={() => handleSelectEventStats(eventItem)}
                                                >
                                                    <div className="stats-sidebar-event-thumb">
                                                        {resolveRenderedEventImageUrl(eventItem) ? (
                                                            <img src={resolveRenderedEventImageUrl(eventItem)} alt={eventItem.name} />
                                                        ) : (
                                                            <span>Brak obrazka</span>
                                                        )}
                                                    </div>
                                                    <div className="stats-sidebar-event-content">
                                                        <div className="stats-sidebar-event-name">{eventItem.name}</div>
                                                        <div className="stats-sidebar-event-organizer">
                                                            {resolvedOrganizerLabel}
                                                        </div>
                                                        <div className="stats-sidebar-event-meta">
                                                            {formatSidebarEventDateLabel(eventItem)}
                                                        </div>
                                                        <div className="stats-sidebar-event-time">{formatSidebarEventTimeLabel(eventItem)}</div>
                                                    </div>
                                                </button>
                                            );
                                        })}

                                        {!eventStatsListLoading && !eventStatsListError && eventStatsListLoadingMore && (
                                            <div className="stats-sidebar-events-empty">Ladowanie kolejnych wydarzen...</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {activeTab === 'Użytkownicy' && (
                            <UsersStatsSection
                                renderRangeFilters={renderRangeFilters}
                                usersRange={usersRange}
                                setUsersRange={setUsersRange}
                                usersYear={usersYear}
                                setUsersYear={setUsersYear}
                                usersMonth={usersMonth}
                                setUsersMonth={setUsersMonth}
                                usersWeekStartDate={usersWeekStartDate}
                                setUsersWeekStartDate={setUsersWeekStartDate}
                                usersWeekEndDate={usersWeekEndDate}
                                setUsersWeekEndDate={setUsersWeekEndDate}
                                usersSummaryByKey={usersSummaryByKey}
                                usersGender={usersGender}
                                setUsersGender={setUsersGender}
                                usersGenderShareData={usersGenderShareData}
                                usersGenderTotal={usersGenderTotal}
                                ageData={ageData}
                                cityData={cityData}
                                usersRegisteredSeries={usersRegisteredSeries}
                                usersNewSeries={usersNewSeries}
                                usersActiveSeries={usersActiveSeries}
                                usersPackSeries={usersPackSeries}
                                usersConfirmedSeries={usersConfirmedSeries}
                                usersAccountDeletionsSeries={usersAccountDeletionsSeries}
                                usersReportsSeries={usersReportsSeries}
                                usersNewDemography={usersNewDemography}
                                usersActiveDemography={usersActiveDemography}
                                usersPackDemography={usersPackDemography}
                            />
                        )}

                        {activeTab === 'Organizatorzy' && (
                            <OrganizersStatsSection
                                renderRangeFilters={renderRangeFilters}
                                organizersRange={organizersRange}
                                setOrganizersRange={setOrganizersRange}
                                organizersYear={organizersYear}
                                setOrganizersYear={setOrganizersYear}
                                organizersMonth={organizersMonth}
                                setOrganizersMonth={setOrganizersMonth}
                                organizersWeekStartDate={organizersWeekStartDate}
                                setOrganizersWeekStartDate={setOrganizersWeekStartDate}
                                organizersWeekEndDate={organizersWeekEndDate}
                                setOrganizersWeekEndDate={setOrganizersWeekEndDate}
                                organizersSummaryByKey={organizersSummaryByKey}
                                organizersKpiCards={organizersKpiCards}
                                organizersRegisteredSeries={organizersRegisteredSeries}
                                organizersNewSeries={organizersNewSeries}
                                organizersKpiByKey={organizersKpiByKey}
                                organizersPurchasedPromotionsSeries={organizersPurchasedPromotionsSeries}
                                organizersPromotionsValueSeries={organizersPromotionsValueSeries}
                            />
                        )}

                        {activeTab === 'Wydarzenia' && (
                            <EventsStatsSection
                                eventsSummaryMode={eventsSummaryMode}
                                setEventsSummaryMode={setEventsSummaryMode}
                                renderRangeFilters={renderRangeFilters}
                                eventsRange={eventsRange}
                                setEventsRange={setEventsRange}
                                eventsYear={eventsYear}
                                setEventsYear={setEventsYear}
                                eventsMonth={eventsMonth}
                                setEventsMonth={setEventsMonth}
                                eventsWeekStartDate={eventsWeekStartDate}
                                setEventsWeekStartDate={setEventsWeekStartDate}
                                eventsWeekEndDate={eventsWeekEndDate}
                                setEventsWeekEndDate={setEventsWeekEndDate}
                                eventsTotalEventsCard={eventsTotalEventsCard}
                                eventsCategoryBreakdownItems={eventsCategoryBreakdownItems}
                                eventsAvgParticipantsCard={eventsAvgParticipantsCardResolved}
                                eventsAvgParticipantsSeries={eventsAvgParticipantsSeries}
                                eventsMetric={eventsMetric}
                                setEventsMetric={setEventsMetric}
                                eventsCategoryFilters={eventsCategoryFilters}
                                selectedEventsCategory={selectedEventsCategory}
                                setEventsCategory={setEventsCategory}
                                eventsInterestItems={eventsInterestItems}
                                eventsTopInterestExpanded={eventsTopInterestExpanded}
                                setEventsTopInterestExpanded={setEventsTopInterestExpanded}
                                handleOpenEventFromStats={handleOpenEventFromStats}
                                toSafeMetricNumber={toSafeMetricNumber}
                                eventsTopPacksItems={eventsTopPacksItems}
                                eventsTopPacksExpanded={eventsTopPacksExpanded}
                                setEventsTopPacksExpanded={setEventsTopPacksExpanded}
                            />
                        )}

                        {activeTab === EVENT_DETAIL_TAB && (
                            <EventDetailsStatsSection
                                eventDetailScopeValue={eventDetailScopeValue}
                                handleEventTimeScopeChange={handleEventTimeScopeChange}
                                selectedEventStatsData={selectedEventStatsData}
                                selectedEventLocation={selectedEventLocation}
                                selectedEventDate={selectedEventDate}
                                eventViewingLegend={eventViewingLegend}
                                eventPacksLegend={eventPacksLegend}
                                eventReturningLegend={eventReturningLegend}
                                setEventInfoModalKey={setEventInfoModalKey}
                                eventInterestBoost={eventInterestBoost}
                                eventViewsTotal={eventViewsTotal}
                                eventPacksTotal={eventPacksTotal}
                                eventConversionRate={eventConversionRate}
                                eventAgeFilter={eventAgeFilter}
                                setEventAgeFilter={setEventAgeFilter}
                                eventAgeData={eventAgeData}
                                eventAgeAxisMax={eventAgeAxisMax}
                                eventVisibleSeries={eventVisibleSeries}
                                handleEventVisibleSeriesToggle={handleEventVisibleSeriesToggle}
                                eventActivitySeries={eventActivitySeries}
                                eventInfoModalContent={eventInfoModalContent}
                                eventInfoModalKey={eventInfoModalKey}
                            />
                        )}

                        {activeTab === 'Rankingi' && (
                            <RankingsStatsSection
                                rankingVisibleList={rankingVisibleList}
                                setRankingVisibleList={setRankingVisibleList}
                                rankingCategories={rankingCategories}
                                rankingCategory={rankingCategory}
                                setRankingCategory={setRankingCategory}
                                rankingRange={rankingRange}
                                setRankingRange={setRankingRange}
                                rankingListConfig={rankingListConfig}
                                setRankingInfoModalOpen={setRankingInfoModalOpen}
                                rankingInfoModalOpen={rankingInfoModalOpen}
                                resolveRenderedEventImageUrl={resolveRenderedEventImageUrl}
                            />
                        )}
                    </div>
                </div>
            </Content>
        </div>
    );
}

export default Stats;
